/**
 * API request/response contracts shared between apps/api (Hono routes) and
 * apps/web (typed fetch client).
 *
 * Keep these in lock-step with `apps/api/src/routes/*`.
 *
 * Conventions:
 *   - Money is INTEGER cents (suffix `_cents`); the frontend formats.
 *   - Hours/percentages/multipliers are floats.
 *   - Pure dates are 'YYYY-MM-DD' strings.
 *   - Timestamps are ISO-8601 strings.
 */

import type {
  BlockStatus,
  Daypart,
  DeliveryStatus,
  FeedbackNoteStatus,
  MaterialQueueStatus,
  ParseStatus,
  PcoBillingType,
  PcoStatus,
  ProjectRole,
  RollupStatus,
  StaleReason,
  SubmissionStatus,
  UserRole,
  VarianceStatus,
} from './status';

export interface HealthResponse {
  status: 'ok';
  service: 'coffman-tm-api';
  time: string;
}

export interface ApiError {
  error: string;
  message?: string;
}

// ----- Auth -----

export interface RequestMagicLinkRequest {
  email: string;
}

export interface RequestMagicLinkResponse {
  ok: true;
}

export interface VerifyMagicLinkRequest {
  token: string;
}

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  role: UserRole;
}

export interface AuthProject {
  id: string;
  project_number: string;
  name: string;
  gc_name: string;
}

export interface VerifyMagicLinkResponse {
  sessionToken: string;
  expiresAt: string; // ISO
  user: AuthUser;
}

/**
 * THE SESSION SHAPE. `POST /api/auth/verify` (magic link), `POST /api/auth/login`
 * (password), `POST /api/auth/accept-invite`, and `POST /api/auth/reset-password`
 * all return exactly this, byte-for-byte, so the client's `setSession` has one
 * code path regardless of how the session was established.
 */
export type SessionResponse = VerifyMagicLinkResponse;

export interface LoginRequest {
  email: string;
  password: string;
}

export type LoginResponse = SessionResponse;

/**
 * Which auth mechanisms the login page should render. Read unauthenticated at
 * `GET /api/auth/config` — it is the ONLY thing that decides whether the
 * magic-link fallback block appears.
 *
 * `magic_link_enabled` mirrors the server's `MAGIC_LINK_ENABLED` var, which
 * FAILS OPEN: absent, empty, or anything other than the literal "false" means
 * enabled. A typo in the var can therefore never lock everyone out mid-cutover.
 */
export interface AuthConfigResponse {
  magic_link_enabled: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

/**
 * ALWAYS `{ok:true}`, whether or not the address belongs to a user — same
 * anti-enumeration posture as `request-magic-link` (11-intentional A2).
 */
export interface ForgotPasswordResponse {
  ok: true;
}

/** `POST /api/auth/accept-invite` and `POST /api/auth/reset-password`. */
export interface SetPasswordRequest {
  token: string;
  password: string;
}

/**
 * Both set-password endpoints sign the caller in on success: the token proved
 * inbox control and the password was just chosen, so bouncing them back to the
 * login form to retype it buys nothing.
 */
export type SetPasswordResponse = SessionResponse;

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  ok: true;
  /** Sessions revoked on other devices. The caller's own session survives. */
  revoked_other_sessions: number;
}

/**
 * Auth error codes. The client should switch on `error`, never on `message`.
 *
 * `invalid_credentials` is deliberately ONE code for wrong email, wrong
 * password, and no-password-set — the response body is byte-identical across
 * all three so the endpoint cannot be used to discover which addresses exist
 * (11-intentional A2). `account_deactivated` is the one distinguished case,
 * and it is only reachable by someone who supplied the CORRECT password.
 *
 * `rate_limited` covers both throttle layers (fixed-window and per-account
 * backoff) on purpose — the same code and the same body shape, so a locked
 * account is indistinguishable from a busy window. Never render it as
 * "account locked"; render the countdown plus a reset link.
 */
export type AuthErrorCode =
  | 'invalid_json'
  | 'invalid_request'
  | 'invalid_credentials'
  | 'account_deactivated'
  | 'invalid_current_password'
  | 'weak_password'
  | 'invalid_token'
  | 'rate_limited'
  | 'magic_link_disabled'
  | 'send_failed'
  | 'internal_error';

export interface AuthErrorResponse {
  error: AuthErrorCode;
  message?: string;
}

/** `429` body for every throttled auth route. */
export interface RateLimitedResponse {
  error: 'rate_limited';
  /** Seconds until the caller may try again. Always ≥ 1, never > 900. */
  retry_after_seconds: number;
}

/** `400 weak_password` body — `problems` mirrors `checkPassword` in shared. */
export interface WeakPasswordResponse {
  error: 'weak_password';
  message: string;
  problems: string[];
}

/**
 * One row of the caller's `project_users` membership list, in the canonical
 * membership order (oldest membership first, id as tiebreak). Enough to render
 * the side-rail project switcher; the full project record for the ACTIVE
 * project still comes back as `project`.
 */
export interface MeProjectMembership {
  project_id: string;
  project_number: string;
  name: string;
}

export interface MeResponse {
  user: AuthUser;
  project: AuthProject;
  /** Role on the ACTIVE project (not "some membership"). */
  role_in_project: ProjectRole;
  /** Every project the caller belongs to, canonical order. Always ≥1 entry. */
  projects: MeProjectMembership[];
  /**
   * The project THIS request resolved to — either the validated `X-Project-Id`
   * header or the canonical-first membership when the header was absent.
   */
  active_project_id: string;
}

// ----- User administration (`/api/users`, admin-only) -----
//
// A user spans projects, so this lives OUTSIDE `/api/settings` even though its
// only UI is the Settings page's Users tab. Every route is behind
// `requireAuth` + `requireAdmin`; a non-admin gets `403 forbidden_not_admin`
// from all of them.
//
// THERE IS NO `DELETE /api/users/:id` AND THERE MUST NOT BE. Deleting a user
// who ever hit Approve & Send fails outright (`rollup_sends.submitted_by_user_id`
// is NOT NULL with no onDelete), a delete CASCADEs away their feedback history,
// and `audit_log.actor_user_id` is SET NULL so every action they ever took goes
// anonymous. "Remove access" is `POST /:id/deactivate`.

/** One project a user can see. Read-only in v1 — memberships are ops-side. */
export interface AdminUserProject {
  project_id: string;
  project_number: string;
  name: string;
}

/**
 * A user as the Users tab renders them. `has_password` and `invite_sent_at`
 * together are the "Invited" state: no password yet, but a live invite is out.
 */
export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  role: UserRole;
  is_active: boolean;
  /** False = invited-but-not-accepted, or still magic-link-only. */
  has_password: boolean;
  /** True while `locked_until` is in the future — the login backoff is biting. */
  is_locked: boolean;
  locked_until: string | null; // ISO
  failed_login_count: number;
  last_login_at: string | null; // ISO
  deactivated_at: string | null; // ISO
  created_at: string; // ISO
  /** Newest unconsumed invite, if one is outstanding. Drives "Invited · sent 2d ago". */
  invite_sent_at: string | null; // ISO
  invite_expires_at: string | null; // ISO
  /** Live (unrevoked, unexpired) sessions — what "Sign out everywhere" would kill. */
  active_session_count: number;
  /** Every project this user can see. Empty means they cannot use the app at all. */
  projects: AdminUserProject[];
  /** True for the caller's own row — the UI disables its destructive controls. */
  is_self: boolean;
  /** True when this is the last ACTIVE admin; demote/deactivate are refused. */
  is_last_admin: boolean;
}

export interface AdminUsersListResponse {
  users: AdminUser[];
}

export interface CreateUserRequest {
  email: string;
  display_name: string;
  role: UserRole;
}

/**
 * `invite_sent` is false when the account was created but the invite email
 * failed to send — the user row exists and Re-invite is the fix. The account is
 * NEVER rolled back on a send failure: a created user with no invite is
 * recoverable in one click, a lost row is not.
 */
export interface CreateUserResponse {
  user: AdminUser;
  invite_sent: boolean;
}

export interface InviteUserResponse {
  user: AdminUser;
  invite_sent: boolean;
  invite_expires_at: string | null; // ISO
}

/** Both fields optional; omitted fields are left alone. */
export interface PatchUserRequest {
  display_name?: string;
  role?: UserRole;
}

export interface AdminUserResponse {
  user: AdminUser;
}

/** `POST /:id/deactivate` — sessions die in the same operation, so the count is reported. */
export interface DeactivateUserResponse {
  user: AdminUser;
  revoked_sessions: number;
}

export interface RevokeUserSessionsResponse {
  user: AdminUser;
  revoked_sessions: number;
}

/** `POST /:id/unlock` — clears the account backoff AND the email-scoped windows. */
export interface UnlockUserResponse {
  user: AdminUser;
  /** True when the account actually held a live lock (vs. a no-op unlock). */
  was_locked: boolean;
}

/**
 * User-admin error codes. The self/last-admin guards are `409`, not `403`:
 * the caller IS authorized, the ACTION is refused, and the UI needs to tell
 * those two apart to render the right sentence.
 */
export type UserAdminErrorCode =
  | 'forbidden_not_admin'
  | 'invalid_json'
  | 'invalid_email'
  | 'invalid_name'
  | 'invalid_role'
  | 'nothing_to_update'
  | 'email_exists'
  | 'user_not_found'
  | 'cannot_change_own_role'
  | 'cannot_deactivate_self'
  | 'last_admin'
  | 'user_inactive'
  | 'already_active'
  | 'already_inactive';

// ----- Settings -----
//
// All Settings endpoints are GET, return arrays scoped to the authenticated
// user's project, and require `Authorization: Bearer <session-token>`.
// Mutation endpoints land in Slice 4.

export interface SettingsEmployee {
  id: string;
  heavyjob_employee_id: string;
  name: string;
  classification: string;
  st_rate_cents: number;
  ot_multiplier: number;
  dt_multiplier: number;
  per_diem_eligible: boolean;
  active: boolean;
  swing_rate_cents: number | null;
  primary_daypart: Daypart;
}

export interface SettingsEquipment {
  id: string;
  asset_id: string;
  description: string;
  type: 'owned' | 'rental';
  hourly_rate_cents: number;
  active: boolean;
}

export interface SettingsMaterial {
  id: string;
  name: string;
  unit: string;
  default_rate_cents: number;
  active: boolean;
}

// ----- Rate comparison across jobs -----
//
// `GET /api/settings/rate-comparison?kind=employees|equipment` — read-only,
// and the ONE settings read that deliberately spans projects: every job the
// caller holds a `project_users` row on, side by side, so a PM can see where a
// person or an asset is carried at a different rate. Nothing here writes; a
// rate is still changed on that job's own roster.

export type RateComparisonKind = 'employees' | 'equipment';

/** One column: a job the caller may read rates on. */
export interface RateComparisonJob {
  project_id: string;
  project_number: string;
  name: string;
}

/**
 * One row: a roster identity as the jobs know it, keyed by the id the parser
 * links on (HeavyJob employee id / asset id).
 *
 * `rates` and `swing` carry an entry for EVERY job in the response — `null`
 * means that job does not carry this row at all, which is a different fact
 * from a rate of zero and reads as a dash on screen. `swing` is present for
 * employees only; equipment has one rate.
 *
 * `swing` is the EFFECTIVE swing rate, i.e. what the job would actually bill a
 * swing hour at — a stored NULL bills at the day rate (`laborBaseRateCents`),
 * so returning null would have printed the "not on this job" dash over a real
 * billing rate and made two jobs that bill swing differently read as agreeing.
 * `swing_implicit` flags the cells where that fallback happened.
 *
 * Both swing fields are returned by the API and NOT shown by the web since
 * 2026-09-21 (swing is too small a part of billing to earn a toggle on that
 * screen); re-add the column if Coffman asks.
 *
 * `labels` is the spelling each job carries for the same id ("S. Calloway" vs
 * "Calloway, Stephen" — both real). `label` is the one the row is named by:
 * the job the reader is standing in when it carries the id, else the first job
 * in column order.
 */
export interface RateComparisonRow {
  key: string;
  label: string;
  labels: Record<string, string>;
  rates: Record<string, number | null>;
  swing?: Record<string, number | null>;
  swing_implicit?: Record<string, boolean>;
}

export interface RateComparisonResponse {
  jobs: RateComparisonJob[];
  rows: RateComparisonRow[];
}

/**
 * Funding tranche — one Hoffman approval (MOD + FWA + dollar) against a
 * parent PCO. 0..N per PCO. See ADR 0002.
 */
export interface FundingTranche {
  id: string;
  pco_id: string;
  mod_number: string | null;
  hoffman_fwa: string | null;
  approved_amount_cents: number;
  /** ISO 'YYYY-MM-DD' or null when the date wasn't recorded. */
  approved_date: string | null;
  /**
   * STORED funded state (migration 0048, client ruling 2026-08-06). Funded is
   * no longer inferred from the presence of a MOD / FWA / date: the GC issues
   * those references before the funding executes, so a PENDING bucket can
   * legitimately carry all three.
   */
  is_funded: boolean;
  comments: string | null;
  /** ISO timestamp. */
  created_at: string;
}

/**
 * Per-PCO money derivations (computed read-time, not stored). See
 * ADR 0002 §Derived money fields.
 *
 *   approved_total = SUM(tranches.approved_amount_cents)
 *   pending        = 0 when the PCO is void, else
 *                    (amount_proposed_cents − approved_total, clamp ≥ 0)
 *                    + pending buckets
 *                    + awaiting approval (money SENT to Hoffman under a
 *                      still-'closed' submission)
 *   billed_to_date = SUM(rollups.invoice_total_cents WHERE status IN ('submitted','approved','paid'))
 *                    + ledger-only imported totals
 *   remaining      = approved_total − billed_to_date (clamp ≥ 0)
 *
 * Pending means "asked for, not yet approved" — Coffman's own PCO Log
 * meaning, which counts submitted-but-unapproved T&M alongside unapproved
 * proposals and blanks void PCOs (money-language audit 2026-07-28). Pending
 * and billed_to_date deliberately overlap: a sent-and-unapproved submission
 * is in both.
 */
export interface PcoMoneyDerived {
  amount_proposed_cents: number | null;
  approved_total_cents: number;
  pending_cents: number;
  /**
   * FUNDING-side pending only: the proposal gap + pending-classified bucket
   * sum, WITHOUT the submitted-awaiting-approval billing arm that
   * `pending_cents` gained 2026-07-28 (and without the void blank — this is
   * the raw funding picture). The derived status chip reads this, not
   * `pending_cents`: a fully funded PCO with billing still awaiting the GC's
   * approval is Funded, not Partially funded. Optional so older
   * clients/fixtures stay valid; consumers fall back to `pending_cents`.
   */
  funding_pending_cents?: number;
  billed_to_date_cents: number;
  remaining_cents: number;
  tranche_count: number;
  /**
   * SUM(approved_amount_cents) over this PCO's PENDING-classified
   * ("amount-only") funding_tranches — folded into `pending_cents` (except
   * on a void PCO, whose pending blanks to 0 wholesale; this stays the raw
   * bucket money either way).
   * Surfaced separately so the frontend's Proposed-stat fallback can render
   * `amount_proposed_cents ?? (approved_total_cents + pending_bucket_cents)`
   * (funding buckets, finding #13). Always 0 for PCOs with no pending-
   * classified tranches — i.e. all existing prod data today. Optional so
   * older clients/fixtures stay valid.
   */
  pending_bucket_cents?: number;
}

/**
 * `mapped_code_count` lets the UI show "5 codes mapped" without the frontend
 * re-querying activity_codes per PCO. Counted via a single GROUP BY server-side.
 *
 * MOD / FWA / approved amount live on `funding_tranches`; `mod_numbers` and
 * `hoffman_fwas` here are convenience joins (approved_date order). Money math
 * (approved_total / pending / billed / remaining) is derived read-time.
 */
export interface SettingsPco {
  id: string;
  pco_number: string;
  description: string;
  status: PcoStatus;
  /**
   * ORGANIZATIONAL ONLY — never a money input. `weekly_rollup` = billed
   * through the portal off foreman PDFs; `lump_sum` = priced outside and
   * hard-entered. Filters the side rail's "Recent PCOs" (rollup-type only);
   * the All-PCOs directory and the PCO Log show both.
   */
  billing_type: PcoBillingType;
  notes: string | null;
  mapped_code_count: number;
  /**
   * Count of submissions on this PCO (any status). Lets list surfaces show an
   * expand affordance for pure-ledger PCOs (imported submissions, zero weekly
   * rollups) that the rollup-derived activity signal misses.
   */
  submission_count: number;
  /** PCO-level comments (display-only on the PCO Log). */
  comments: string | null;
  void_reason: string | null;
  is_no_cost: boolean;
  /**
   * Workflow nudge: true when a PDF block was saved to this PCO and nobody
   * has opened it since — one shared flag for the project, not a per-user
   * unread state. Drives the side rail's Recent-PCOs ranking (attention
   * bucket).
   */
  has_unreviewed_blocks: boolean;
  /** Convenience: MODs / FWAs joined from tranches in approved_date order. */
  mod_numbers: string[];
  hoffman_fwas: string[];
  /** Derived money fields. */
  amount_proposed_cents: number | null;
  approved_total_cents: number;
  pending_cents: number;
  /**
   * FUNDING-side pending only (proposal gap + pending buckets; no
   * awaiting-approval billing arm, no void blank). The derived status chip
   * reads this instead of `pending_cents` — see PcoMoneyDerived. Optional;
   * consumers fall back to `pending_cents`.
   */
  funding_pending_cents?: number;
  billed_to_date_cents: number;
  remaining_cents: number;
  tranche_count: number;
  /**
   * SUM(approved_amount_cents) over PENDING-classified ("amount-only")
   * funding_tranches — inside `pending_cents` (except on a void PCO, whose
   * pending blanks to 0 wholesale; the raw bucket money still reports here).
   * Surfaced so the PCO
   * Detail "Proposed" stat can fall back to `approved_total_cents +
   * pending_bucket_cents` once `amount_proposed_cents` is retired to null
   * on first Edit-Details save (funding buckets, finding #13). Always 0 for
   * PCOs with no pending-classified tranches (all existing prod data).
   */
  pending_bucket_cents?: number;
  /**
   * Count of this PCO's in_progress weekly rollups. Rail-ranking signal:
   * "has an open week right now".
   */
  open_week_count: number;
  /**
   * MAX(week_ending_date) over the PCO's member rollups; null when the PCO has
   * no weekly rollups (ledger-only or unbilled). Rail-ranking recency signal —
   * real-world week dates, deliberately not row timestamps (backfill stamped
   * all updated_at values identically).
   */
  latest_week_ending_date: string | null;
  /**
   * ISO creation timestamp. Rail-recency signal: a newly created PCO counts as
   * recent. Backfilled rows carry a normalized pre-window date so the import
   * batch doesn't read as "just created".
   */
  created_at: string;
}

/**
 * Project-level money roll-up surfaced on the PCO Log top strip. See
 * ADR 0002 §Derived money fields.
 */
export interface ProjectTotals {
  original_contract_cents: number;
  approved_co_to_date_cents: number;
  revised_contract_cents: number;
  pending_co_to_date_cents: number;
  pending_contract_cents: number;
  billed_to_date_cents: number;
}

export interface FundingTrancheWriteRequest {
  mod_number?: string | null;
  hoffman_fwa?: string | null;
  approved_amount_cents: number;
  approved_date?: string | null;
  /**
   * Funded / pending. Independent of the references above. On CREATE, omitting
   * it falls back to the pre-0048 rule (any reference present ⇒ funded) so an
   * older API caller still lands the row it meant to; on PATCH, omitting it
   * leaves the stored state alone like every other field here.
   */
  is_funded?: boolean;
  comments?: string | null;
}

/**
 * `mapped_pco` is null when the activity code has been seen by the parser but
 * not yet assigned to a PCO — these are the "needs PCO" rows in the mockup.
 */
export interface SettingsActivityCode {
  id: string;
  code: string;
  description_from_pdf: string | null;
  first_seen_at: string; // ISO
  last_seen_at: string; // ISO
  mapped_pco: { id: string; pco_number: string; description: string } | null;
  /**
   * IGNORE list (2026-07-31). Non-null = this code is contract / lump-sum work
   * that never bills here, so its blocks skip the PDF Inbox entirely. ISO
   * timestamp of when it was ignored.
   *
   * MUTUALLY EXCLUSIVE with `mapped_pco`: when this is set, `mapped_pco` is
   * always null and the server refuses to map the code until it's un-ignored.
   */
  ignored_at: string | null;
  /** `users.display_name` of whoever ignored it; null when unknown/deleted. */
  ignored_by_name: string | null;
}

/** Body of `PATCH /api/settings/activity-codes/:id/ignore`. */
export interface ActivityCodeIgnoreRequest {
  ignored: boolean;
}

/**
 * Result of ignoring / un-ignoring an activity code.
 *
 * `blocks_changed` is the retroactive half: ignoring converts the code's
 * still-`pending` blocks to `'ignored'` (never a `saved` one — that's billed
 * work), un-ignoring restores every `'ignored'` block to `'pending'`.
 */
export interface ActivityCodeIgnoreResponse {
  ok: true;
  ignored: boolean;
  blocks_changed: number;
}

export interface SettingsProject {
  id: string;
  project_number: string;
  name: string;
  gc_name: string;
  gc_email: string;
  /**
   * Roster of additional Approve & Send recipients beyond `gc_email` (client
   * decision 2026-07-22 — Joey sends rollups to a few people on Hoffman's
   * cost team). Editable in Settings; capped at `MAX_GC_ADDITIONAL_EMAILS`.
   * Today every address here receives every Approve & Send email alongside
   * `gc_email` — a follow-up feature adds per-send checkboxes to pick a
   * subset, at which point this becomes the checkbox roster. See
   * projects.gc_additional_emails.
   */
  gc_additional_emails: string[];
  /** Human PM identity used to compose outbound Approve & Send email (From
   *  display name, Reply-To, CC). See projects.pm_name / pm_email. */
  pm_name: string;
  pm_email: string;
  markup_pct: number;
  /** Equipment Usage Tax %, applied to the equipment subtotal only. 0 = off
   *  (row hidden on the rollup). */
  equipment_tax_pct: number;
  per_diem_rate_cents: number;
  /**
   * Amber reminder for Settings → Equipment: set by the New Project wizard
   * when equipment was copied with the source job's rates ("Rates copied from
   * {number} · {date}"), nulled by the Dismiss PATCH, re-set by nothing.
   * NULL = no banner.
   */
  equipment_rates_review_note: string | null;
  per_diem_unit: string;
  original_contract_amount_cents: number | null;
  /**
   * Default employee ST rate. New employees prefill from this; the Settings
   * UI's "Apply to all employees" action bulk-PATCHes existing rows.
   * Davis-Bacon prevailing-wage flat rate on Micron today ($106.43).
   */
  default_st_rate_cents: number;
  /** Default employee swing rate. New swing-rated employees prefill from this. */
  default_swing_rate_cents: number;
  default_per_diem_eligible: boolean;
  /**
   * When true, labor bills the conventional ST/OT/DT premium split. When
   * false, all hours bill flat at the ST rate (Coffman/Hoffman 25132).
   * UI-positive mirror of the stored `bill_overtime_as_st` flag
   * (calculate_ot_dt === !bill_overtime_as_st).
   */
  calculate_ot_dt: boolean;
  /**
   * Sunday premium: when on, LABOR dated a Sunday bills at
   * `sunday_premium_rate_cents` instead of the employee's day/swing rate.
   * A substituted BASE rate, not a multiplier — it never stacks with OT/DT,
   * never touches equipment, and leaves per diem alone. Both fields must be
   * set (and the rate positive) for the rule to apply.
   */
  sunday_premium_enabled: boolean;
  /** Sunday base rate in integer cents. Null = never configured. */
  sunday_premium_rate_cents: number | null;
}

// ----- Settings: unknown-but-seen surface -----
//
// The parser links each labor row to an `employees` row (and each equipment
// row to an `equipment` row) when it can match by heavyjob_employee_id /
// asset_id; otherwise the FK is NULL and the raw name from the PDF is
// preserved. These endpoints surface those NULL-FK rows so Joey can add the
// missing entity to the roster (or correct a typo in an existing one).
//
// One row per RAW IDENTIFIER, not per name: RTLD622 and RTLD623 are both
// "624P LOADER -- COASTLINE" and each needs its own row to be addable. Rows
// whose identifier is null (FLEET TRUCKS category lines, labor with no HJ id)
// still group by name — there's nothing finer to key on.

export interface UnknownEmployee {
  /**
   * Stable identity of this row — `id:<heavyjob_employee_id_raw>` when the
   * parser captured one, else `name:<employee_name_raw>`. Use it as the list
   * key and as the selection handle; the name alone is NOT unique. Opaque:
   * read the fields below, don't parse this.
   */
  row_key: string;
  employee_name_raw: string;
  occurrences: number;
  first_seen: string; // 'YYYY-MM-DD'
  last_seen: string; // 'YYYY-MM-DD'
  /** Most-recent PDF this identity appeared in, for one-click context lookup. */
  latest_daily_pdf_id: string | null;
  /**
   * HeavyJob employee ID for this row — the identity it's grouped by, not a
   * name-latest guess. Prefills the Add-to-roster form so the new employee's
   * HJ ID matches the canonical key the parser will use to link future PDFs
   * (and to backfill historical rows on create). Null when the parser couldn't
   * pull an ID (or on rows ingested before the raw column landed in schema
   * migration 0004), in which case this row is a name-grouped fallback.
   */
  heavyjob_employee_id_raw: string | null;
}

export interface UnknownEquipment {
  /**
   * Stable identity of this row — `id:<asset_id_raw>` when the parser captured
   * one, else `name:<equipment_name_raw>`. Two assets can share a description,
   * so the name alone is NOT unique. Opaque: read the fields below, don't
   * parse this.
   */
  row_key: string;
  equipment_name_raw: string;
  occurrences: number;
  first_seen: string;
  last_seen: string;
  /** Most-recent PDF this identity appeared in, for one-click context lookup. */
  latest_daily_pdf_id: string | null;
  /**
   * Asset ID for this row — the identity it's grouped by. Same role as the
   * employee HJ ID counterpart: canonical key for FK linkage + form prefill.
   * Null on category lines (FLEET TRUCKS) and legacy rows, which fall back to
   * name grouping.
   */
  asset_id_raw: string | null;
}

/**
 * Activity codes the parser has seen on at least one PDF but no PCO mapping
 * exists for yet. Same id/code/description as `SettingsActivityCode` minus
 * `mapped_pco` (always null here by definition).
 *
 * IGNORED codes are EXCLUDED from this list (2026-07-31). This array is the
 * "Activity codes without a PCO" to-do list and its count is a warning badge;
 * an ignored code is a decided code, so leaving it here would make a finished
 * job read as outstanding forever.
 */
export interface UnknownActivityCode {
  id: string;
  code: string;
  description_from_pdf: string | null;
  first_seen_at: string;
  last_seen_at: string;
  /** Most-recent PDF this code appeared in, for one-click context lookup. */
  latest_daily_pdf_id: string | null;
}

export interface SettingsUnknownsResponse {
  employees: UnknownEmployee[];
  equipment: UnknownEquipment[];
  activity_codes: UnknownActivityCode[];
}

// ----- Settings: remembered crew-slot placeholders -----
//
// One foreman's HeavyJob export prints crew slots in the labor rows
// (`LABORER-ID11`, `OPER-IDPF`) instead of people, so nothing on the roster
// matches. The PM decides once in the PDF Inbox and intake repeats it; this
// list is where those decisions are visible and forgettable.
//
// Per job. A decision made on one job carries no authority on another —
// rates and rosters are per job (client ruling 2026-09-21).

/** GET /api/settings/foreman-placeholders — one remembered decision. */
export interface SettingsForemanPlaceholder {
  id: string;
  foreman_hj_id: string;
  /** As printed on the report when the decision was made. Display only. */
  foreman_name: string;
  /** The slot id from the report's ID column, trimmed. */
  placeholder_id: string;
  /** The target, by HeavyJob id — what the decision actually stores. */
  heavyjob_employee_id: string;
  /**
   * The target's CURRENT roster name, or null when nobody on this job's roster
   * holds that HeavyJob id any more. Null means the decision resolves to
   * nothing and lines carrying the slot stay unknown.
   */
  employee_name: string | null;
  /**
   * Whether the target is active on this job's roster. False (or a null name)
   * means the decision is inert — a fill would have no rate, so intake leaves
   * the line unknown instead.
   */
  employee_active: boolean;
  /** ISO timestamp of the most recent decision for this slot. */
  updated_at: string;
}

// ----- Settings mutations -----
//
// Same shapes as the read responses, minus server-managed fields (id,
// created_at, mapped_code_count, etc.). All are project-scoped via the
// authenticated user's project on the server.

/**
 * Max entries accepted in `gc_additional_emails` — "a couple of people on
 * the cost team", not a distribution list. Enforced server-side
 * (apps/api/src/settings/recipients.ts); the web form uses this same
 * constant so the add-row control disables before a save would 400.
 */
export const MAX_GC_ADDITIONAL_EMAILS = 5;

export interface ProjectPatchRequest {
  name?: string;
  gc_name?: string;
  gc_email?: string;
  /** Full replacement of the additional-recipients roster — omit to leave
   *  unchanged; send [] to clear it. Validated + de-duped server-side. */
  gc_additional_emails?: string[];
  pm_name?: string;
  pm_email?: string;
  markup_pct?: number;
  equipment_tax_pct?: number;
  per_diem_rate_cents?: number;
  per_diem_unit?: string;
  original_contract_amount_cents?: number | null;
  default_st_rate_cents?: number;
  default_swing_rate_cents?: number;
  default_per_diem_eligible?: boolean;
  /** UI-positive mirror of bill_overtime_as_st; server stores the inverse. */
  calculate_ot_dt?: boolean;
  sunday_premium_enabled?: boolean;
  /** Integer cents; null clears the configured rate. */
  sunday_premium_rate_cents?: number | null;
}

/**
 * The two halves of the Sunday premium. The rule needs BOTH set to apply, so a
 * save that carries one without the other leaves the premium half-on: the
 * server reads the pair as inert and Sunday hours bill the ordinary rate, while
 * the PM has been told the premium is on. Lives here rather than on the web
 * because it is a statement about the WIRE — a sender that omits one half is
 * sending something the server cannot act on. The changed-fields-only save
 * reads it and sends both whenever either moved.
 */
export const PROJECT_PATCH_FIELD_PAIRS = [
  ['sunday_premium_enabled', 'sunday_premium_rate_cents'],
] as const satisfies readonly (readonly [keyof ProjectPatchRequest, keyof ProjectPatchRequest])[];

/**
 * PATCH /api/settings/project — the refreshed row, exactly as GET returns it.
 */
export interface ProjectPatchResponse extends SettingsProject {
  /**
   * The edit LANDED but something downstream of it did not — the job's open
   * weeks were not re-totalled, its sent weeks were not flagged, or its roster
   * did not take the new swing standard. Ops was paged. Not a failed save, so
   * the response is a 200 and carries the row. ABSENT on a clean save, so the
   * happy-path response is byte-identical to what it has always been.
   */
  consequences_failed?: boolean;
}

/**
 * POST /api/settings/employees/apply-defaults
 *
 * Bulk-PATCH employees to the project's current defaults. Caller picks which
 * fields to apply via the booleans — Joey might want to flatten rates but
 * leave per-diem flags alone, or vice versa. Returns the updated employee
 * list so the UI can refresh without a second round trip.
 */
export interface ApplyEmployeeDefaultsRequest {
  apply_st_rate: boolean;
  apply_per_diem_eligible: boolean;
  /** Optional so existing callers (pre-swing-rate) stay valid unchanged. */
  apply_swing_rate?: boolean;
}

export interface ApplyEmployeeDefaultsResponse {
  /** Number of employee rows touched. */
  updated_count: number;
  employees: SettingsEmployee[];
}

/**
 * POST /api/settings/apply-rates-to-saved — "Apply to saved blocks" on the
 * Employees / Equipment tabs.
 *
 * A ONE-TIME apply: on this job's saved blocks whose week is still open
 * (rollup in_progress, or none yet), every rostered row whose stamped
 * `confirmed_rate_cents` differs from the rate the roster gives it today is
 * re-stamped at that rate. The rows stay frozen at the applied rate — a later
 * Settings edit reprices nothing saved. `confirm: false` is a preview and
 * writes nothing.
 */
export type ApplyRatesToSavedKind = 'employees' | 'equipment';

export interface ApplyRatesToSavedRequest {
  kind: ApplyRatesToSavedKind;
  confirm: boolean;
}

/**
 * One open week the apply touches — one per ROLLUP, so a split week with two
 * open packages is two entries. `rollup_id` is null for a week whose rollup
 * does not exist yet; `submission_number` is the package's "n" in "PCO 004.n"
 * when it has one.
 */
export interface ApplyRatesWeekRef {
  pco_id: string;
  pco_number: string;
  week_ending_date: string;
  rollup_id: string | null;
  submission_number: string | null;
}

export interface ApplyRatesWeekDelta extends ApplyRatesWeekRef {
  /** Σ confirmed hours × (new rate − old rate), rounded once. Before markup. */
  delta_cents: number;
  /** The week's rollup was sent before and has since been reopened. */
  sent_before: boolean;
}

export interface ApplyRatesToSavedResponse {
  /** Rows whose rate changes (preview) or changed (confirm). */
  rows: number;
  /** Distinct saved blocks those rows sit on. */
  blocks: number;
  /** Distinct open weekly rollups (or rollup-less weeks) those blocks bill in. */
  weeks: number;
  /** Σ over every changed row of confirmed hours × (new − old). Before markup / tax. */
  delta_cents: number;
  /** Per (PCO, week), ordered by week then PCO number. */
  week_deltas: ApplyRatesWeekDelta[];
  /** Of `weeks`, how many were sent before and are now reopened. */
  sent_before_weeks: number;
  /** Rows on open weeks with a stamped rate but no roster match — left alone. */
  skipped_unmatched_rows: number;
  /**
   * Open (PCO, week)s with saved work, whether or not anything in them
   * changes. Zero means everything saved has been sent — the dialog says that
   * rather than "already matches".
   */
  open_weeks: number;
  /** Confirm only: open rollups re-totalled. */
  recomputed_rollups?: number;
  /** Confirm only: weeks whose re-total failed. Their rates DID change. */
  failed_weeks?: ApplyRatesWeekRef[];
  /** Confirm only: weeks sent between the read and the write — left untouched. */
  skipped_locked_weeks?: ApplyRatesWeekRef[];
  /**
   * Confirm only: rows whose rate someone else changed between the read and
   * the write — the old-rate guard left them alone, and they are not in the
   * counts above or in the audit row.
   */
  skipped_changed_rows?: number;
}

export interface EmployeeWriteRequest {
  heavyjob_employee_id: string;
  name: string;
  classification: string;
  st_rate_cents: number;
  ot_multiplier?: number;
  dt_multiplier?: number;
  per_diem_eligible?: boolean;
  active?: boolean;
  swing_rate_cents?: number | null;
  primary_daypart?: Daypart;
}

export interface EquipmentWriteRequest {
  asset_id: string;
  description: string;
  type: 'owned' | 'rental';
  hourly_rate_cents: number;
  active?: boolean;
}

export interface MaterialWriteRequest {
  name: string;
  unit: string;
  default_rate_cents: number;
  active?: boolean;
}

export interface PcoWriteRequest {
  pco_number: string;
  description: string;
  status?: PcoStatus;
  /**
   * Omitted on create → the server defaults to `lump_sum` (the safe
   * majority). Omitted on update → the stored value is preserved.
   */
  billing_type?: PcoBillingType;
  notes?: string | null;
  amount_proposed_cents?: number | null;
  comments?: string | null;
  void_reason?: string | null;
  is_no_cost?: boolean;
}

export interface ActivityCodeAssignRequest {
  /** PCO id to map, or null to unmap (return to "needs assignment"). */
  mapped_pco_id: string | null;
  /**
   * Set to true to apply the remap even when locked rollups would be
   * affected. The server marks each affected rollup as stale (with
   * reason='code_remapped') and returns the list so the UI can confirm.
   * Without `force`, a 409 with the affected list is returned instead.
   */
  force?: boolean;
}

/**
 * 200 response shape on a successful activity-code remap. The remap is
 * forward-only: it updates the code's mapping and refreshes the suggested PCO
 * on still-pending blocks, but never touches saved or submitted (billed) work.
 * `pending_blocks_updated` is how many pending blocks had their suggestion
 * re-pointed — drives the informational "Mapping updated. N pending blocks
 * updated." confirmation.
 */
export interface CodeRemapSuccessResponse {
  ok: true;
  pending_blocks_updated: number;
}

// ----- Materials Queue -----
//
// Each item is one quantity-bearing line the parser pulled out of a foreman's
// notes (e.g. "280 ton roadbase", "180 ft of 16-inch C-900 pipe"). Joey
// reviews the queue daily — confirming what's right, editing what needs fixing,
// skipping what isn't billable.
//
// `quoted_text` is preserved verbatim from the PDF as the audit trail back to
// the source. `suggested_*` is the parser's match against materials_master
// (the price list); `confirmed_*` is what Joey approved (may differ).

export interface MaterialQueueMaterialRef {
  id: string;
  name: string;
  unit: string;
  default_rate_cents: number;
}

export interface MaterialQueueDailyEntryContext {
  id: string;
  report_date: string; // 'YYYY-MM-DD'
  activity_code: string;
  pco: { id: string; pco_number: string; description: string } | null;
  daily_pdf: {
    id: string;
    foreman_name: string | null;
    email_from: string;
  };
}

export interface MaterialQueueItem {
  id: string;
  status: MaterialQueueStatus;
  quoted_text: string;
  suggested_material: MaterialQueueMaterialRef | null;
  suggested_quantity: number | null;
  suggested_unit: string | null;
  confirmed_material: MaterialQueueMaterialRef | null;
  confirmed_quantity: number | null;
  confirmed_rate_cents: number | null;
  confirmed_at: string | null; // ISO
  confirmed_by_user_id: string | null;
  created_at: string; // ISO
  updated_at: string; // ISO
  daily_entry: MaterialQueueDailyEntryContext;
}

export interface MaterialQueueListResponse {
  items: MaterialQueueItem[];
}

/**
 * All fields optional. Anything omitted falls back to the parser's
 * `suggested_*` values — the one-click confirm path.
 */
export interface MaterialQueueConfirmRequest {
  material_id?: string;
  quantity?: number;
  rate_cents?: number;
}

export interface MaterialQueueItemResponse {
  item: MaterialQueueItem;
}

// ----- Weekly Rollups -----
//
// One persisted row per (PCO, week-ending Saturday). Auto-recomputed after
// every PDF parse and every materials confirm/skip/reset. `RollupListItem` is
// the lightweight list shape (totals only, no line breakdown — fast for the
// dashboard's "active PCOs this week" card). `RollupDetail` has the full
// breakdown — same shape the xlsx renderer consumes server-side.

export interface RollupTotals {
  labor_cents: number;
  equipment_cents: number;
  materials_cents: number;
  markup_cents: number;
  /** Subcontractor lines (outside-sub vendor invoices billed as lump lines).
   *  Joins the Material/Equip/Subs bucket: inside the markup base, never
   *  equipment-taxed, no per diem. 0 on rollups with no subcontractor lines. */
  subcontractors_cents: number;
  /** Equipment Usage Tax on the equipment subtotal only; inside the markup
   *  base (markup is computed on labor+equipment+materials+subs+tax). 0 when
   *  the project tax % is 0. */
  equipment_tax_cents: number;
  per_diem_cents: number;
  invoice_total_cents: number;
}

/** One daily report that landed after the week had already gone to the GC. */
export interface RollupRevisionLatePdf {
  /** Whose report it is. NULL when the parse never resolved a foreman — the
   *  date still identifies the report, so the name is dropped, not faked. */
  foreman_name: string | null;
  report_date: string; // 'YYYY-MM-DD'
  received_at: string; // ISO instant the PDF reached intake
}

/**
 * WHAT LANDED, on a week that reopened for revision.
 *
 * Between the auto-reopen and the resend the week is live again and its total
 * has left Submitted-to-date, but nothing said WHY: the PM saw a generic
 * banner and a number that had moved. This is the answer — when the GC was
 * last sent this week, what he was told it cost, and which reports arrived
 * after that.
 *
 * Present ONLY while the rollup is in the reopened-for-revision state
 * (`status === 'in_progress'` + `stale_after_submission` +
 * `stale_reason === 'late_pdf_received'`) and has a send behind it. Omitted
 * entirely otherwise — never sent as an empty object, so a consumer can branch
 * on presence alone.
 */
export interface RollupRevisionContext {
  /** When the GC was last sent this week (newest `rollup_sends` row). */
  prior_sent_at: string;
  /**
   * The invoice total that send carried. NULL on sends that predate the
   * column — treat as "not recorded" and omit the comparison; a $0 baseline
   * would read as the week having been sent empty.
   */
  prior_invoice_total_cents: number | null;
  /** The reports received after `prior_sent_at`, in report-date order. */
  late_pdfs: RollupRevisionLatePdf[];
}

export interface RollupListItem {
  id: string;
  submission_id: string;
  submission_number: string | null;
  pco: { id: string; pco_number: string; description: string };
  week_ending_date: string; // 'YYYY-MM-DD'
  status: RollupStatus;
  submitted_at: string | null;
  approved_at: string | null;
  paid_at: string | null;
  /** True for a backfill-imported historical rollup (frozen totals, no lines). */
  imported: boolean;
  /** True when an original xlsx is stored in R2 and downloadable. */
  has_original_xlsx: boolean;
  /**
   * The week's data has diverged from what was sent to the GC. Combined with
   * `status === 'in_progress'` this is the **reopened-for-revision** state: a
   * late PDF auto-reopened the week (2026-07-29), its money has left
   * Submitted-to-date, and it needs resending. On a still-sent week it means
   * "sent, then something changed" — reopen and resend.
   *
   * Optional on the wire so a response from an API older than the field parses.
   */
  stale_after_submission?: boolean;
  /** Why it went stale — `late_pdf_received` is the auto-reopen case. */
  stale_reason?: StaleReason | null;
  /** What landed since the last send. Present only in the reopened-for-revision
   *  state; see RollupRevisionContext. */
  revision_context?: RollupRevisionContext;
  totals: RollupTotals;
  updated_at: string;
}

export interface RollupListResponse {
  rollups: RollupListItem[];
}

/**
 * Detail shape returned by GET /api/rollups/weekly/:pcoId/:weekEndingDate.
 * Mirrors the server-side `RollupDetail` (apps/api/src/rollups/compute.ts)
 * with Date fields serialized to ISO strings. The frontend doesn't
 * recompute totals — server is the source of truth.
 */
/**
 * Provenance for a manually-added line (ADR 0004). `is_manual` is true when the
 * line came from a manual add (no foreman PDF). The metadata drives the
 * "MANUALLY ADDED" badge tooltip. All null/false on PDF-sourced lines.
 */
export interface ManualProvenance {
  is_manual: boolean;
  manual_added_by: string | null;
  manual_reason: string | null;
  manual_added_at: string | null; // ISO
}

export interface RollupDetailLaborLine extends ManualProvenance {
  employee_id: string | null;
  /** HeavyJob ID — primary identifier surfaced in the UI ahead of name. */
  heavyjob_employee_id: string | null;
  employee_name: string;
  classification: string;
  st_hours: number;
  ot_hours: number;
  dt_hours: number;
  st_rate_cents: number;
  ot_rate_cents: number;
  dt_rate_cents: number;
  per_diem_eligible: boolean;
  is_unknown: boolean;
}

export interface RollupDetailEquipmentLine extends ManualProvenance {
  equipment_id: string | null;
  asset_id: string;
  description: string;
  hours: number;
  rate_cents: number;
  is_unknown: boolean;
}

export interface RollupDetailMaterialLine extends ManualProvenance {
  material_queue_item_id: string;
  material_id: string | null;
  name: string;
  quantity: number;
  unit: string;
  rate_cents: number;
}

/**
 * A subcontractor line — an outside sub's vendor invoice billed as a lump on
 * this rollup (e.g. "Brix Paving - Pervious Pavement"). Rollup-level: no
 * source PDF, no block, always PM-added by hand, so `is_manual` is
 * literally `true` (the ManualProvenance badge renders on every line).
 */
export interface RollupDetailSubcontractorLine {
  id: string;
  description: string;
  /** Lump amount in integer cents, non-zero; negative = a vendor credit. */
  amount_cents: number;
  /** 'YYYY-MM-DD' — inside the rollup's Sun–Sat window. */
  invoice_date: string;
  is_manual: true;
  manual_added_by: string | null;
  manual_reason: string | null;
  manual_added_at: string | null; // ISO
  /**
   * Whether this line has its vendor invoice PDF attached. A line IS one
   * vendor invoice (ADR 0009), so it holds at most one — Approve & Send
   * packages it alongside the daily T&M PDFs.
   *
   * Derived from the stored R2 key; the key itself is NEVER exposed. Viewing
   * goes through `GET …/subcontractors/:lineId/invoice/url`, which mints the
   * same kind of short-lived presigned URL daily PDFs use. Optional on the
   * wire so a response from an API older than the column still parses.
   */
  has_invoice_pdf?: boolean;
}

// ----- Manual row additions (ADR 0004) -----
//
// Add a billable line to a rollup when the foreman PDF didn't carry it. Creates
// a daily_entries row with source='manual' (null pdf/activity FKs) + a child row
// with the confirmed-pair invariant. report_date MUST be inside the rollup's
// Sun–Sat window. Blocked when the rollup is locked (submitted/approved/paid).

export interface ManualLaborAddRequest {
  /** Roster FK when known; else null and employee_name carries a free-text name. */
  employee_id?: string | null;
  employee_name?: string;
  report_date: string; // 'YYYY-MM-DD', must be in the rollup week
  total_hours: number; // > 0
  rate_cents: number; // >= 0 integer
  reason: string; // 10–280 chars
}

export interface ManualEquipmentAddRequest {
  equipment_id?: string | null;
  asset_id?: string; // free-text fallback
  report_date: string;
  hours: number;
  rate_cents: number;
  reason: string;
}

export interface ManualMaterialAddRequest {
  material_id?: string | null;
  /** Free-text description (writes to material_queue_items.quoted_text). */
  quoted_text: string;
  unit?: string;
  report_date: string;
  quantity: number; // > 0
  rate_cents: number; // per-unit rate, >= 0 integer
  reason: string;
}

export interface ManualAddResponse {
  ok: true;
  daily_entry_id: string;
}

// ----- Subcontractor lines -----
//
// Rollup-level lump lines for outside-sub vendor invoices. Canonical
// addressing only (POST /api/rollups/by-rollup/:rollupId/subcontractors,
// PATCH/DELETE .../subcontractors/:lineId) — no legacy (pco, week) variant.
// Blocked when the rollup is locked (409 rollup_locked); invoice_date must be
// inside the rollup's Sun–Sat window.

export interface SubcontractorLineAddRequest {
  /** 1–200 chars after trim. */
  description: string;
  /** Integer cents, non-zero. Negative = a vendor credit, entered as its own
   *  line with its own document (2026-09-25). Zero → 400 invalid_amount. */
  amount_cents: number;
  /** 'YYYY-MM-DD', inside the rollup week. */
  invoice_date: string;
  /** Optional justification (≤ 280 chars); blank treated as absent. */
  reason?: string;
}

/** Omitted fields are left unchanged; at least one must be present. */
export interface SubcontractorLinePatchRequest {
  description?: string;
  amount_cents?: number;
  invoice_date?: string;
}

export interface SubcontractorLineResponse {
  ok: true;
  line: RollupDetailSubcontractorLine;
}

/**
 * Vendor-invoice PDF on a subcontractor line (2026-07-29). A line IS one
 * vendor invoice, so it carries at most one PDF and re-uploading REPLACES it
 * (same R2 key, overwritten).
 *
 *   PUT    /api/rollups/by-rollup/:rollupId/subcontractors/:lineId/invoice
 *          Raw `application/pdf` body (no multipart). → SubcontractorLineResponse
 *   DELETE /api/rollups/by-rollup/:rollupId/subcontractors/:lineId/invoice
 *          Clears the column and deletes the R2 object. → SubcontractorLineResponse
 *   GET    /api/rollups/by-rollup/:rollupId/subcontractors/:lineId/invoice/url
 *          → SignedPdfUrlResponse (15-min presigned URL, inline disposition)
 *
 * Guards: PUT and DELETE are line edits, so they 409 `rollup_locked` on any
 * non-`in_progress` rollup (imported included) exactly like add/patch/remove.
 * GET is allowed on a locked rollup — reviewing what was sent is legitimate.
 * PUT rejects a body that isn't a PDF (`400 invalid_pdf`, checked on the
 * `%PDF-` magic bytes, not the Content-Type header a client controls) or
 * exceeds the size cap (`400 invoice_too_large`).
 */
export const SUBCONTRACTOR_INVOICE_MAX_BYTES = 10 * 1024 * 1024;

export type SubcontractorInvoiceErrorCode =
  | 'rollup_not_found'
  | 'rollup_locked'
  | 'line_not_found'
  | 'invalid_pdf'
  | 'invoice_too_large'
  | 'no_invoice';

export interface RollupContributingPdf {
  daily_pdf_id: string;
  report_date: string; // 'YYYY-MM-DD'
  foreman_name: string | null;
  email_from: string;
  /** Used to derive the human-readable filename label client-side. */
  email_subject: string;
}

export interface RollupDetailTotals extends RollupTotals {
  per_diem_hours: number;
}

/**
 * Body for POST /api/rollups/weekly/:pcoId/:weekEndingDate/reopen.
 * Optional Joey-supplied note kept in the audit_log (after_json.reason).
 */
export interface ReopenRollupRequest {
  reason?: string;
}

export interface ReopenRollupResponse {
  rollup_id: string;
  status: 'in_progress';
  reopened_at: string; // ISO-8601
}

export interface ApproveRollupResponse {
  rollup_id: string;
  status: 'approved';
  approved_at: string; // ISO-8601
}

/**
 * CORRECT AN IMPORTED WEEK'S FROZEN TOTAL.
 *
 * An imported (`imported = 1`) weekly rollup is backfill history: zero member
 * entries, a frozen `invoice_total_cents`, and the original workbook in R2.
 * Every recompute path skips it and Reopen refuses it outright, which is
 * correct — there are no lines to rebuild from. But the figure itself can be
 * wrong, because it was typed off a pre-portal spreadsheet, and until now the
 * only fix was an ops-side D1 UPDATE.
 *
 * This is that fix, self-serve. The TOTAL only: no week, no dates, no
 * reopening, and no recompute — the derived numbers that read this (the
 * package total, the PCO's cumulative, Submitted-to-date) all recompute on
 * read.
 */
export interface EditImportedTotalRequest {
  /** The corrected figure. Integer cents, > 0 — same rule as a ledger-only
   *  submission's amount; v1 has no credits. */
  invoice_total_cents: number;
  /** WHY, and it is required. The figure being replaced exists nowhere else
   *  once it is overwritten, so the audit row carrying this reason is the only
   *  account of what changed and on whose say-so. Blank or whitespace is
   *  rejected. */
  reason: string;
}

export interface EditImportedTotalResponse {
  rollup_id: string;
  week_ending_date: string; // 'YYYY-MM-DD'
  invoice_total_cents: number;
  /** What it was, so the caller can state the correction without re-reading. */
  previous_invoice_total_cents: number;
  corrected_at: string; // ISO-8601
}

/**
 * Body for POST /api/rollups/{weekly/…,by-rollup/…}/close-without-send.
 * Same optional note as reopen, kept in the audit_log (after_json.reason).
 */
export interface CloseWithoutSendRequest {
  reason?: string;
}

/**
 * A reopened week finalized with NO email (2026-08-06).
 *
 * The rollup returns to `submitted` — money back in Submitted-to-date, blocks
 * re-locked, totals frozen — and NO `rollup_sends` row is written, because
 * nothing was sent. `last_sent_at` is the send the GC is still holding: the
 * copy this revision now supersedes without replacing.
 *
 * There is no `submitted_at` in the response because the server deliberately
 * leaves that column NULL — it means "when a copy of THIS version reached the
 * GC", and the answer is never.
 */
export interface CloseWithoutSendResponse {
  rollup_id: string;
  status: 'submitted';
  closed_at: string; // ISO-8601
  /** `submitted_at` of the newest prior send — the version the GC has. */
  last_sent_at: string; // ISO-8601
  invoice_total_cents: number;
}

// ----- Submission history -----
//
// Every Approve & Send creates a row in `submissions`. Reopening a rollup
// doesn't delete prior rows — they're the audit record of what was sent to
// Hoffman. The history panel surfaces every submission with its own xlsx
// artifact (preserved in R2) and the resend message id.

/**
 * ONE DOCUMENT AS A SEND ACTUALLY CARRIED IT (migration 0050, 2026-08-06).
 *
 * The per-send answer `generated_documents` structurally cannot give: that
 * table records which BYTES were produced, and a byte-identical resend reuses
 * the row rather than writing a second one, so the relation is
 * many-sends-to-one-artifact. This record is written at send time from the very
 * arrays handed to Resend, so it says what one specific email attached.
 *
 * Frozen forensics, not a live join. Ids here are recorded for navigation
 * (opening the artifact, naming the daily) and carry no referential integrity
 * on purpose: a daily_pdfs row can be deleted by a reparse reconcile, and the
 * record of what the GC received must survive that.
 */
export interface RollupSendDocument {
  /**
   * `workbook` — the rendered Hoffman xlsx (exactly one per send).
   * `daily`    — one foreman daily report.
   * `invoice`  — one subcontractor line's vendor invoice.
   */
  kind: 'workbook' | 'daily' | 'invoice';
  /** The name the GC's inbox shows. Not unique across kinds by construction —
   *  a regenerated daily ships under the original's filename. */
  filename: string;
  /** R2 object key the bytes came from (a daily/invoice) or were stored at
   *  (the workbook). Never exposed to the GC; the download routes own that. */
  r2_key: string;
  /** `daily` only — the foreman report this page is. */
  daily_pdf_id?: string;
  /**
   * `daily` only — WHICH COPY went out. This is the fact the week page's
   * "Generated PDF" column could not previously prove for the `original`
   * case (`11-intentional.md` M14).
   */
  source?: 'original' | 'regenerated';
  /** `daily` + `source: 'regenerated'` — the `generated_documents` row whose
   *  bytes rode, so the compare viewer can open exactly that copy. */
  generated_document_id?: string | null;
  /** `invoice` only — the `rollup_subcontractor_lines` row that owns it. */
  subcontractor_line_id?: string;
}

export interface SubmissionHistoryItem {
  id: string;
  submitted_at: string; // ISO
  /** Every address this send went to (gc_email + additional recipients at
   *  send time), comma-joined — same convention as SubmitRollupResponse. */
  recipient_email: string;
  /**
   * The CC header as actually sent, comma-joined — the composer's per-send Cc
   * list plus the PM's own copy (2026-08-06). Display-only, not a parseable
   * list, exactly like `recipient_email`. NULL on every send that predates the
   * column, and on any send with no CC at all.
   */
  cc_email: string | null;
  subject: string;
  attached_pdf_count: number;
  resend_message_id: string | null;
  delivery_status: DeliveryStatus;
  is_revision: boolean;
  /** ID of the prior submission this revision supersedes, when applicable. */
  supersedes_submission_id: string | null;
  xlsx_r2_key: string;
  /**
   * Every document this send attached, in send order (workbook first).
   *
   * **NULL means NOT RECORDED, never "nothing attached"** — every send that
   * predates migration 0050 has no capture, and a stored value we cannot parse
   * degrades to the same honest null rather than to a shorter list. The UI
   * says so out loud; it must never render an empty attachment list for a send
   * whose contents simply were not written down. Same convention as
   * `rows_snapshot` and `cc_email`.
   */
  documents: RollupSendDocument[] | null;
}

export interface SubmissionsListResponse {
  submissions: SubmissionHistoryItem[];
}

// ----- Submissions (PCO → submission → weekly rollup) -----
//
// A submission is Coffman's real billing package: 1..N weekly rollups under one
// number (002.6), MOD, FWA, and approval status. Date range + total are DERIVED
// from member rollups. Distinct from the send-audit `SubmissionHistoryItem`
// above (that's `rollup_sends`).

export interface SubmissionSummary {
  id: string;
  submission_number: string;
  status: SubmissionStatus;
  is_default_target: boolean;
  mod_number: string | null;
  hoffman_fwa: string | null;
  comments: string | null;
  opened_at: string; // ISO
  closed_at: string | null;
  approved_at: string | null;
  /** Derived min/max member week; null when the submission has zero rollups. */
  week_range: { first_week: string; last_week: string } | null;
  /** SUM(member rollups' invoice_total_cents). */
  total_cents: number;
  /** Ledger-only backfill total (PCO Log amount) when the submission has no member rollups; else null. */
  imported_total_cents: number | null;
  /** The PCO Log's literal date-range text for a ledger-only submission; else null. */
  imported_range_label: string | null;
  rollups: RollupListItem[];
}

/**
 * GET /api/submissions — every submission on the project with member rollups,
 * grouped by PCO. One request powers the PCO Log's expand-in-place so row
 * expansion never waits on the network.
 */
export interface ProjectSubmissionsResponse {
  pcos: Array<{ pco_id: string; submissions: SubmissionSummary[] }>;
}

export interface PcoSubmissionsResponse {
  /**
   * The parent PCO's number, for label rendering ("PCO 004.7"). Sourced at
   * the response level so zero-rollup submissions (fresh close-and-next /
   * open-alongside) still label correctly.
   */
  pco_number: string;
  submissions: SubmissionSummary[];
}

/**
 * POST /api/pcos/:pcoId/submissions/close-and-next
 *
 * Closing carries every unsent (in_progress) member week of the closing
 * submission into the next one — sent/approved/imported weeks stay behind as
 * the closed package. If next_number names an OPEN carve-out already on the
 * PCO, that submission is ADOPTED as the new default instead of inserting a
 * fresh one (carried weeks land in it).
 *
 * Errors: `{ error: SubmissionErrorCode }` — 'no_open_default' when the PCO
 * has no open default submission; 'number_conflict' when next_number is taken
 * by a non-open submission (or the closing one itself); 'carry_week_conflict'
 * when an adopted carve-out already bills a carried week (nothing mutated).
 */
export interface CloseAndStartNextRequest {
  next_number?: string;
  mod_number?: string | null;
  hoffman_fwa?: string | null;
}

/** Errors: `{ error: SubmissionErrorCode }` — see that union for the full set. */
export interface CloseAndStartNextResponse {
  closed: SubmissionSummary;
  opened: SubmissionSummary;
  /** How many unsent (in_progress) weeks moved from `closed` into `opened`. */
  carried_week_count: number;
}

/** POST /api/pcos/:pcoId/submissions (carve-out: open alongside, NOT default). */
export interface OpenSubmissionRequest {
  number?: string;
  mod_number?: string | null;
  hoffman_fwa?: string | null;
}

/**
 * POST /api/pcos/:pcoId/submissions/start-next — **Start Next**.
 *
 * The blessed model (2026-07-29): *Start Next opens the PCO's next submission
 * as the new collecting home — closes nothing, carries nothing; Approve & Send
 * is what closes a package, offered on the last unsent week of ANY open
 * submission.*
 *
 * So this route opens the next-numbered submission, makes it the PCO's
 * `is_default_target` (new saved weeks land there), and **leaves the previous
 * default open and untouched** — no close, no week carry. Every field is an
 * optional override:
 *
 *   number       absent → MAX(numeric)+1 across the PCO
 *   mod_number   absent → INHERITED from the most recent prior submission
 *   hoffman_fwa  absent → INHERITED likewise
 *
 * An explicit `null` on a ref means "no ref", not "inherit".
 *
 * Errors: `{ error: SubmissionErrorCode }` — 'pco_not_found' (404);
 * 'number_conflict' (409) when an explicit `number` is already used on the PCO.
 */
export interface StartNextSubmissionRequest {
  number?: string;
  mod_number?: string | null;
  hoffman_fwa?: string | null;
}

/** The submission Start Next created (already the PCO's default target). */
export type StartNextSubmissionResponse = SubmissionResponse;

/**
 * POST /api/pcos/:pcoId/submissions/historical — **Add historical submission**.
 *
 * The PM's catch-up path for billing that predates the portal: one row off the
 * PCO Log becomes a **ledger-only** submission — an amount and a free-text
 * worked range, no weekly rollups, no PDFs, nothing emailed. The amount enters
 * billed-to-date (and, while `closed`, the Pending awaiting arm) through the
 * ledger arms of the PCO derive, which count `imported_total_cents` only while
 * the submission has zero member rollups.
 *
 * It is the one creation path that inherits nothing — no MOD/FWA carry from a
 * prior submission (each log row carries its own refs) — and it never takes
 * `is_default_target`: there is no field for it, and the server hardcodes
 * false so a frozen ledger total can never become the home new saved work
 * flows into.
 *
 * Errors: `{ error: code }`, 400 unless noted —
 *   'pco_not_found' (404) · 'invalid_json' · 'body_required' ·
 *   'number_required' (blank/absent number) ·
 *   'number_conflict' (409 — compared NUMERICALLY, so '12' clashes with an
 *   existing '012') · 'invalid_amount' (not an integer > 0; credits are out of
 *   scope in v1) · 'invalid_status' · 'invalid_range_label' (over 120 chars) ·
 *   'invalid_reference' (non-string mod_number / hoffman_fwa).
 */
export interface AddHistoricalSubmissionRequest {
  /** The ".N". Required, unique per PCO under numeric comparison. */
  number: string;
  /** Integer cents, must be > 0. The PCO Log's amount for this row. */
  imported_total_cents: number;
  /**
   * Never 'open' — an open submission counts on neither derive arm, so a
   * historical row created open would be money that appears nowhere.
   * 'closed' = sent, awaiting the GC; 'approved' = approved to bill.
   */
  status: 'closed' | 'approved';
  /** The log's literal worked-date range ("3.21.26 - 4.11.26"); blank → null. */
  imported_range_label?: string | null;
  /** Zero-padded to 3 digits when purely numeric, like every MOD write. */
  mod_number?: string | null;
  hoffman_fwa?: string | null;
}

/** The ledger-only submission that was created. */
export type AddHistoricalSubmissionResponse = SubmissionResponse;

/**
 * POST /api/rollups/by-rollup/:rollupId/move-submission
 *
 * Re-home one unsent week — the whole rollup; member blocks ride along via
 * their weekly_rollup_id pins — into another OPEN submission on the same PCO.
 * Exactly one of target_submission_id / create_next.
 *
 * Errors: `{ error: SubmissionErrorCode }` — 'rollup_not_found' (404) /
 * 'rollup_locked' (409) when the rollup isn't in_progress;
 * 'submission_not_found' (404) / 'invalid_destination_submission' (409, other
 * PCO or a ledger-only backfill package) / 'submission_not_open' (409) on a
 * bad target; 'week_conflict' (409) when the target submission already bills
 * that week.
 */
export interface MoveRollupSubmissionRequest {
  target_submission_id?: string;
  /**
   * Start the PCO's NEXT package and move the week into it: opens the
   * next-numbered submission, INHERITING mod_number / hoffman_fwa from the
   * most recent prior, and makes it the PCO's new **default target** so
   * subsequent saves follow it. The previously-default submission stays OPEN
   * (demoted, never closed) until the PM closes it via
   * POST /api/submissions/:id/close.
   *
   * The `target_submission_id` path, by contrast, never touches the default —
   * parking a week in a side package must not re-route new work.
   */
  create_next?: boolean;
}

export interface MoveRollupSubmissionResponse {
  ok: true;
  rollup_id: string;
  week_ending_date: string;
  target_submission_id: string;
  target_submission_number: string;
  /** True when create_next opened the target in the same call. */
  created_next: boolean;
}

/**
 * Every error code the submission surfaces can return in `{ error: code }`.
 * One union so dialogs can exhaustively switch on it.
 *
 * Lifecycle codes (thrown by apps/api/src/submissions/lifecycle.ts; the HTTP
 * status rides on the error — 404 / 400 / 409 as noted):
 *   - 'submission_not_found'        — id not in this project (404)
 *   - 'submission_not_open'         — assign target isn't status 'open' (409)
 *   - 'no_open_default'             — close-and-next found no open default
 *                                     submission on the PCO (400)
 *   - 'number_conflict'             — submission_number already used on this
 *                                     PCO (409)
 *   - 'number_locked'               — submission_number edit on a non-open
 *                                     submission (409)
 *   - 'illegal_status_transition'   — status flip outside the allowed set,
 *                                     see SubmissionPatchRequest.status (400)
 *   - 'source_week_locked'          — a touched week holds a non-in_progress
 *                                     rollup; reopen it first (409)
 *   - 'carry_week_conflict'         — close-and-next: the adopted carve-out
 *                                     already bills a carried unsent week;
 *                                     nothing was mutated (409)
 *   - 'rollup_locked'               — move-submission: the rollup isn't
 *                                     in_progress; reopen it first (409)
 *   - 'week_conflict'               — move-submission: the target submission
 *                                     already bills that week (or already
 *                                     owns this rollup) (409)
 *   - 'rollup_not_found'            — move-submission: the rollup id doesn't
 *                                     resolve in this project (404)
 *   - 'not_ledger_submission'       — the target isn't a ledger-only
 *                                     historical row (an imported total with
 *                                     ZERO member rollups). PATCH of
 *                                     imported_total_cents / imported_range_label
 *                                     (400) and DELETE (409) both use it
 *
 * Route-level codes from the move/assign surface (both 400, except
 * move-submission's 409 reuse of 'invalid_destination_submission' for a
 * target on another PCO):
 *   - 'invalid_destination_submission'      — POST /api/blocks/:id/move:
 *     destination_submission_id doesn't exist / isn't on the destination
 *     PCO / isn't open
 *   - 'submission_required_for_split_week'  — POST /api/blocks/:id/move: the
 *     destination (PCO, week) is split across submissions and no
 *     destination_submission_id was given
 */
export type SubmissionErrorCode =
  | 'submission_not_found'
  | 'submission_not_open'
  | 'no_open_default'
  | 'number_conflict'
  | 'number_locked'
  | 'illegal_status_transition'
  | 'source_week_locked'
  | 'carry_week_conflict'
  | 'rollup_locked'
  | 'rollup_not_found'
  | 'week_conflict'
  | 'invalid_destination_submission'
  | 'submission_required_for_split_week'
  /**
   * The target isn't a ledger-only historical row — PATCH of the imported
   * amount/range (400) and DELETE of the row (409).
   */
  | 'not_ledger_submission'
  /**
   * DELETE of an EMPTY package refused on the row's own state (409): the
   * submission isn't `open`, or it holds `is_default_target`. The message
   * names which.
   */
  | 'not_deletable_submission'
  /**
   * DELETE of an empty package refused because it holds member weeks (409).
   * A package with weeks is billing data; send, transfer or reopen them —
   * removing the package would orphan them.
   */
  | 'submission_has_weeks'
  /**
   * A ledger-only row may not be reopened (409). `closed → open` is legal for
   * an ordinary package but would drop a historical row's money out of BOTH
   * derive arms and unlock block-assignment into it, which is unrecoverable
   * in-app. closed ↔ approved stays available.
   */
  | 'ledger_status_locked'
  // Body-validation codes — POST …/submissions/historical, plus
  // 'number_required' on the PATCH renumber. All 400.
  /** The request body wasn't an object, or a field had an unusable type. */
  | 'body_required'
  /** Blank / absent / non-string submission number. */
  | 'number_required'
  /** imported_total_cents isn't an integer > 0 (no credits in v1). */
  | 'invalid_amount'
  /** status wasn't 'closed' or 'approved' ('open' is deliberately excluded). */
  | 'invalid_status'
  /** imported_range_label is over 120 chars (rejected, never truncated). */
  | 'invalid_range_label'
  /** mod_number / hoffman_fwa weren't strings. */
  | 'invalid_reference'
  /** The :pcoId in the path isn't a PCO in the caller's project. */
  | 'pco_not_found'
  /** POST /api/submissions/:id/close — the submission isn't `open`. */
  | 'not_open'
  /** POST /api/submissions/:id/close — an in_progress member week blocks it. */
  | 'unsent_weeks_exist';

/**
 * POST /api/submissions/:submissionId/close
 *
 * Close ONE open submission — default or not — without opening a next one.
 * Sets `status='closed'` + `closed_at`, and clears `is_default_target` if it
 * held it. Empty body; responds with the updated `SubmissionSummary`.
 *
 * This is what makes the demote-only default swap safe. Transfer Week's
 * `create_next` and the inbox save's `start_next_submission` move the default
 * OFF the current package and leave it open; `close-and-next` can only close
 * the open DEFAULT, so without this route a demoted package could never be
 * closed — and since the Pending awaiting arm counts only `closed`
 * submissions, its money would silently stop being reported as awaiting the
 * GC's approval.
 *
 * Errors: `{ error: SubmissionErrorCode }` — 'submission_not_found' (404);
 * 'not_open' (409) when the submission isn't open (closed → approved is the
 * PATCH route's job); 'unsent_weeks_exist' (409) when any member rollup is
 * still `in_progress` — a close must not entomb an editable week, so the PM
 * sends it or Transfers it out first. Both guards run pre-mutation.
 */
export type CloseSubmissionResponse = SubmissionResponse;

/**
 * PATCH /api/submissions/:submissionId
 *
 * Editability rules (enforced by apps/api/src/submissions/lifecycle.ts;
 * violations return `{ error: SubmissionErrorCode }`):
 *   - Omitted fields are left unchanged; explicit null clears a nullable field.
 */
export interface SubmissionPatchRequest {
  /**
   * Editable ONLY while status === 'open' — any change on a closed/approved
   * submission is 409 'number_locked'. Must be unique per PCO, else 409
   * 'number_conflict'.
   */
  submission_number?: string;
  /** Always editable, any status. */
  mod_number?: string | null;
  /** Always editable, any status. */
  hoffman_fwa?: string | null;
  /** Always editable, any status. */
  comments?: string | null;
  /**
   * Legal transitions (anything else is 400 'illegal_status_transition'):
   *   - open     → closed
   *   - closed   → approved
   *   - closed   → open      (reopen — the submission comes back as NON-default:
   *                           is_default_target is forced false, so it can never
   *                           collide with the PCO's current open default)
   *   - approved → closed    (un-approve)
   *   - same → same is a no-op, always accepted
   * Side effects: → 'approved' stamps approved_at; → 'closed' stamps closed_at.
   */
  status?: SubmissionStatus;
  /**
   * Correct a LEDGER-ONLY historical row's amount (integer cents > 0). 400
   * 'invalid_amount' on a bad value; 400 'not_ledger_submission' when the
   * target isn't ledger-only (an imported total with zero member rollups) —
   * on a rollup-backed submission the column is dead weight the derive
   * ignores, so accepting it would look like a correction that moved no money.
   */
  imported_total_cents?: number;
  /**
   * The same carve-out for the free-text worked range (≤ 120 chars; blank →
   * null). 400 'invalid_range_label' / 'not_ledger_submission'.
   */
  imported_range_label?: string | null;
}

/**
 * DELETE /api/submissions/:submissionId
 *
 * Removes ONE submission that has nothing behind it. TWO shapes qualify, and
 * the route picks between them off the row itself — a submission carrying an
 * `imported_total_cents` is always judged as a ledger row, never as an empty
 * package:
 *
 *   1. **Ledger-only historical row** — the self-serve fix for a pre-portal
 *      package entered twice: an imported total, zero member weeks. Both
 *      `closed` and `approved` ones delete (a duplicate can sit in either).
 *   2. **Empty package** (2026-08-24) — `open`, zero member weeks, not the
 *      PCO's `is_default_target`, no imported total. This is the package a
 *      PCO's FIRST SAVED BLOCK auto-opens, left behind when the PM then starts
 *      the real number (which takes over as the collecting default) and the
 *      week moves onto it. It bills nothing, has no PDFs and no send record,
 *      and it squats on a number.
 *
 * Everything else is refused. Errors: `{ error: SubmissionErrorCode }` —
 * 'submission_not_found' (404); 'not_ledger_submission' (409) when a row with
 * an imported total also has member rollups or still holds the default flag;
 * 'not_deletable_submission' (409) when an ordinary package isn't `open` or is
 * the default target; 'submission_has_weeks' (409) when it holds member weeks.
 * Every guard is pre-mutation.
 */
export interface DeleteSubmissionResponse {
  ok: true;
  /** Which shape was removed — 'ledger' (historical row) or 'empty' (shell). */
  shape: 'ledger' | 'empty';
}

/** Errors: `{ error: SubmissionErrorCode }` — see that union for the full set. */
export interface SubmissionResponse {
  submission: SubmissionSummary;
}

export interface RollupDetail {
  project: { id: string; number: string; name: string; gc_name: string };
  pco: {
    id: string;
    number: string;
    description: string;
    /** Joined from funding_tranches in approved_date order. */
    mod_numbers: string[];
    hoffman_fwas: string[];
  };
  week_ending_date: string;
  markup_pct: number;
  equipment_tax_pct: number;
  per_diem_rate_cents: number;
  labor: RollupDetailLaborLine[];
  equipment: RollupDetailEquipmentLine[];
  materials: RollupDetailMaterialLine[];
  /** Subcontractor lump lines (rollup-level, always manual). */
  subcontractors: RollupDetailSubcontractorLine[];
  totals: RollupDetailTotals;
  contributing_pdfs: RollupContributingPdf[];
  unknown_employee_count: number;
  unknown_equipment_count: number;
  /**
   * @deprecated 2026-08-03 (register entry M12) — served, never read.
   *
   * Count of material_queue_items still in status='pending' for this
   * (PCO, week). It used to power the send-readiness indicator on the week's
   * card. The parser's material suggestions were retired as a review step, so
   * saved work now carries undecided candidates permanently and this number
   * says nothing about whether a week is ready. Kept on the wire only so an
   * older client cannot break on a missing field.
   */
  materials_pending_count: number;
  rollup_id: string | null;
  /** True for a backfill-imported historical rollup (frozen totals, no lines). */
  imported: boolean;
  /** True when an original xlsx is stored in R2 and downloadable. */
  has_original_xlsx: boolean;
  /** The submission this rollup belongs to (null only for orphan/legacy rows). */
  submission: {
    id: string;
    submission_number: string;
    status: SubmissionStatus;
    mod_number: string | null;
    hoffman_fwa: string | null;
  } | null;
  status: RollupStatus;
  submitted_at: string | null;
  approved_at: string | null;
  paid_at: string | null;
  /**
   * Staleness flag — true only when this rollup is locked (status !=
   * in_progress) AND something has happened to the underlying data
   * since submit (late PDF, code remap, late material confirm). The
   * totals shown here are the frozen snapshot of what was sent to
   * Hoffman; the UI surfaces a banner + Reopen action when set.
   */
  stale_after_submission: boolean;
  stale_reason: StaleReason | null;
  /** What landed since the last send. Present only in the reopened-for-revision
   *  state; see RollupRevisionContext. */
  revision_context?: RollupRevisionContext;
}

// ----- Rollup source blocks (ADR 0003 reassignment) -----
//
// The Weekly Rollup detail page lists the individual blocks (daily_entries)
// that make up the rollup so the PM can reassign a mis-routed one. One row per
// member block; the Reassign action opens the destination picker.

export interface RollupBlockSummary {
  daily_entry_id: string;
  activity_code: string;
  /** Activity-code description from the PDF ("EXCAVATE FOR DUCT BANK").
   *  Null when the code has no description on file — the UI falls back to
   *  the code itself as the primary text. */
  description: string | null;
  report_date: string; // 'YYYY-MM-DD'
  current_pco_id: string | null;
  /** Source PDF context — the row is identified by these (date/foreman/filename),
   *  matching the look of the old PDFs section. */
  daily_pdf_id: string;
  foreman_name: string | null;
  /** Used with foreman_name + report_date to derive the display filename client-side. */
  email_subject: string;
  /** Sum of effective labor hours across the block's labor rows. */
  labor_hours: number;
  /** Sum of effective equipment hours across the block's equipment rows. */
  equipment_hours: number;
  /** Count of labor lines on the block (crew members — "6 workers"). */
  labor_count: number;
  /** Count of equipment lines on the block ("3 machines"). */
  equipment_count: number;
  /** Count of confirmed/edited material lines on the block. */
  materials_count: number;
  /**
   * Direct-cost subtotal for the block: labor + equipment at the saved
   * confirmed hours × rates, plus confirmed/edited materials. Pre-markup /
   * per-diem / tax — a recognition figure for pickers, not an invoice line.
   */
  subtotal_cents: number;
}

export interface RollupBlocksResponse {
  rollup_status: RollupStatus;
  /** True when blocks here may be reassigned (rollup is in_progress or submitted). */
  reassignable: boolean;
  blocks: RollupBlockSummary[];
}

// ----- Day-explicit line edits (2026-07-31) -----
//
// The Labor / Equipment tables on Rollup Detail show WEEKLY aggregates, but the
// money is stored per day and the Hoffman xlsx writes one column per day. So an
// edit has to name the DAY, not just the line: `GET .../line-rows` returns the
// underlying per-day rows behind every line, and the edit endpoints take the
// exact `row_id` the PM picked out of them.

/** One underlying per-day child row behind an on-screen rollup line. */
export interface RollupLineDayRow {
  /** daily_labor_entries.id / daily_equipment_entries.id — the edit target. */
  row_id: string;
  daily_entry_id: string;
  report_date: string; // 'YYYY-MM-DD'
  /** Activity code of the parent block ('—' when the block is a manual add). */
  activity_code: string;
  /**
   * The full compute.ts grouping key (labor: identity + daypart + rate triple +
   * manual provenance). Two rows sharing it are one screen line.
   */
  line_key: string;
  /**
   * The part of `line_key` an on-screen line can reconstruct from its own
   * fields — no daypart, which the detail payload doesn't carry. One display
   * key can therefore span a day row and a swing row; the picker shows both
   * with their shift so the PM resolves it explicitly.
   */
  display_key: string;
  /** Labor only. */
  daypart?: Daypart;
  /** Effective hours this row bills today (confirmed pair when set). */
  hours: number;
  /** Effective rate in cents this row bills today. */
  rate_cents: number;
  /** True when the row already carries a confirmed (hours, rate) pair. */
  is_confirmed: boolean;
  is_manual: boolean;
  /**
   * No roster row behind this one (never matched, or the FK dangles). Its
   * effective rate is 0 by construction, and saving that 0 as a confirmed pair
   * would bill the day at nothing forever — so the edit form requires a real
   * rate here, and the server refuses `rate_required_unknown_identity`.
   */
  is_unknown: boolean;
}

export interface RollupLineRowsResponse {
  rollup_id: string | null;
  week_ending_date: string;
  status: RollupStatus;
  /** False when the rollup is locked — the form opens read-only/not at all. */
  editable: boolean;
  labor: RollupLineDayRow[];
  equipment: RollupLineDayRow[];
}

/**
 * Body for the four line-edit endpoints (labor/equipment × hours/rate, both the
 * legacy `(pco, week)` and canonical `by-rollup/:id` registrations).
 *
 * Values are ABSOLUTE for the named day — never a delta against the weekly
 * line total. Both columns of the confirmed pair are written together, since
 * billing only honors an override when both are present.
 */
export interface RollupLineEditRequest {
  /** The exact per-day row from `line-rows`. Required — there is no group-level target. */
  row_id: string;
  /** New hours for that day. Omit to leave the day's hours as they are. */
  hours?: number;
  /** New rate in cents for that day. Omit to keep the row's current effective rate. */
  rate_cents?: number;
  /**
   * Apply `rate_cents` to every day of the SAME line (identity + shift + rate
   * bucket + provenance), not just the named day — the whole-week rate
   * correction. Hours are never spread; only the named day's hours move.
   */
  apply_rate_to_all_days?: boolean;
  /**
   * COMPARE-AND-SWAP preconditions: the target day's hours / rate exactly as
   * `line-rows` last served them. If the row has moved since, the edit is
   * refused with `409 line_changed` (body carries `current`) rather than
   * applied.
   *
   * Send them. The form prefills from the loaded row and posts BOTH values, so
   * without a precondition a rate-only fix silently restates stale hours and an
   * hours-only fix silently restates a stale rate — as a CONFIRMED pair, which
   * outranks every later computation. With two PMs on one week that is a lost
   * update that nothing reports and the spreadsheet bills.
   *
   * Optional only so a deliberate ops/script caller can say "make it this
   * regardless"; omitting them is last-write-wins.
   */
  expected_hours?: number;
  expected_rate_cents?: number;
}

/** 409 body when a compare-and-swap precondition fails. */
export interface RollupLineEditConflict {
  error: 'line_changed';
  hint?: string;
  /** What the day holds NOW — show it rather than making the PM go look. */
  current?: { hours: number; rate_cents: number };
}

export interface RollupLineEditResponse {
  ok: true;
  /** The day the edit landed on. */
  row_id: string;
  report_date: string;
  rows_updated: number;
  previous: { hours: number; rate_cents: number };
  updated: { hours: number; rate_cents: number };
  /**
   * The line's totals after the edit — what the screen row will read once the
   * detail refetches. Informational only: `line_total_cents` is Σ(hours × rate)
   * over the line's rows, which is the billed figure under `bill_overtime_as_st`
   * (Coffman) but does NOT carry OT/DT premium for an unconfirmed row on a
   * project billing premiums. Never bill off it — the rollup detail totals are
   * the money.
   */
  line_total_hours: number;
  line_total_cents: number;
  /** True when the request asked for values the row already had. */
  no_change?: true;
}

// ----- Feedback notes (trial-week feedback feature) -----
//
// Joey writes free-form notes from a floating bubble; the client auto-captures
// route + entity IDs + viewport. Brandon reviews in /admin/feedback. Append-only
// from the user side; admin actions only change status + response, never the note.

export interface FeedbackNote {
  id: string;
  user_id: string;
  /** Author display name, joined server-side for the admin view. */
  user_name: string;
  project_id: string;
  note_text: string;
  page_route: string;
  page_label: string;
  /** Raw JSON string of parsed entity IDs (or null). UI parses on read. */
  page_context_json: string | null;
  viewport_width: number | null;
  viewport_height: number | null;
  status: FeedbackNoteStatus;
  brandon_response: string | null;
  created_at: string; // ISO
  reviewed_at: string | null; // ISO
}

export interface CreateFeedbackNoteRequest {
  note_text: string;
  page_route: string;
  page_label: string;
  /** Entity IDs parsed from the route; keys present only when found. */
  page_context_json?: Record<string, string> | null;
  viewport_width: number;
  viewport_height: number;
}

export interface CreateFeedbackNoteResponse {
  id: string;
}

export interface FeedbackNotesListResponse {
  notes: FeedbackNote[];
}

export interface FeedbackNotePatchRequest {
  status?: FeedbackNoteStatus;
  brandon_response?: string;
}

export interface FeedbackNoteResponse {
  note: FeedbackNote;
}

// ----- Reconciliation -----
//
// Tracks the gap between what Coffman billed (coffman_amount_cents) and what
// Hoffman recorded as paid/approved (hoffman_amount_cents). Joey enters the
// Hoffman number; the server computes variance_cents = coffman - hoffman.
//
// Phase 2 v1: variance is always tied to a specific submitted weekly_rollup
// (one (PCO, week) tuple). Phase 3 may add aggregate-PCO reconciliation if
// Hoffman starts giving Coffman lump-sum disputes.

export interface VarianceRecord {
  id: string;
  status: VarianceStatus;
  pco: { id: string; pco_number: string; description: string };
  weekly_rollup: {
    id: string;
    week_ending_date: string;
    /**
     * The member rollup's submission label ("n" in "PCO {parent}.{n}"), fed
     * from the submission's submission_number. Field name retained here pending
     * a future reconciliation-surface rename.
     */
    sub_pco_number: string | null;
  } | null;
  coffman_amount_cents: number;
  hoffman_amount_cents: number;
  /** coffman - hoffman. Positive = Hoffman underpaid, negative = overpaid. */
  variance_cents: number;
  resolution_notes: string | null;
  resolved_by_user_id: string | null;
  discovered_at: string; // ISO
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface VarianceListResponse {
  variances: VarianceRecord[];
}

export interface VarianceCreateRequest {
  /** Required — variance is per-(PCO, week). */
  weekly_rollup_id: string;
  /** What Hoffman recorded; cents. coffman_amount + variance computed server-side. */
  hoffman_amount_cents: number;
  /** Optional opening note ("Hoffman flagged $X discrepancy in 4/3 review meeting"). */
  resolution_notes?: string;
}

export interface VariancePatchRequest {
  status?: VarianceStatus;
  resolution_notes?: string;
  /** If Hoffman reposts a different number, update + recompute variance. */
  hoffman_amount_cents?: number;
}

export interface VarianceItemResponse {
  variance: VarianceRecord;
}

// ----- Approve & Send -----

/**
 * Max length of the per-send `custom_message` (preview-composer, client
 * decision 2026-07-22). "A revision note or a short paragraph", not a second
 * cover letter — enforced server-side (submit.ts trims to this) and mirrored
 * by the composer textarea's maxLength so the two never disagree.
 */
export const MAX_CUSTOM_MESSAGE_LEN = 2000;

/**
 * Max length of a per-send `body_override` (the composer's editable body,
 * 2026-07-24). Generous — the full rendered letter plus edits fits easily —
 * but bounded so a runaway paste can't ship a megabyte email. Enforced
 * server-side (submit.ts truncates to this) and mirrored by the composer
 * textarea's maxLength so the two never disagree.
 */
export const MAX_BODY_OVERRIDE_LEN = 10000;

/**
 * Max length of a per-send `subject_override` (the composer's editable subject
 * line, 2026-08-02). A subject is one line, not a paragraph — 200 characters is
 * roughly double the canonical rendered subject and still short of any mail
 * client's display truncation being the PM's problem. Enforced server-side
 * (submit.ts sanitizes + caps) and mirrored by the composer input's maxLength
 * so the two never disagree.
 */
export const MAX_SUBJECT_OVERRIDE_LEN = 200;

export interface SubmitRollupRequest {
  /** Override Hoffman's recipient. Omit to use project.gc_email. OPS path —
   *  replaces the whole recipient set (roster + composer selection skipped),
   *  so it is **admin-only** (`403 forbidden_not_admin` otherwise) and must be
   *  a well-formed address (`400 invalid_recipient_override`). No UI sends it. */
  recipient_email_override?: string;
  /** Render everything but skip Resend send + DB writes. Returns
   *  `would_send: true` so the UI can preview before committing. */
  dry_run?: boolean;
  /**
   * Preview-composer: the exact set of addresses this send goes to. Each entry
   * must be the primary `gc_email`, a `gc_additional_emails` roster entry, or
   * equal to `recipient_extra` — the server rejects any unknown address. Omit
   * to fall back to the default set (primary + full roster), which keeps every
   * pre-composer caller byte-identical.
   */
  recipients_selected?: string[];
  /**
   * Preview-composer: one address added for THIS send only. It does NOT join
   * the persisted roster; it only widens the allow-list for
   * `recipients_selected` validation. Ignored under `recipient_email_override`.
   *
   * SUPERSEDED by `recipients_extra` (2026-08-02) now that the composer's
   * type-to-add field can chip more than one off-roster address in a single
   * send. Still accepted, still honored, and merged with the array — an older
   * client (or a script) sending only this field behaves exactly as before.
   */
  recipient_extra?: string;
  /**
   * Preview-composer: EVERY address added for THIS send only (2026-08-02).
   * Same contract as `recipient_extra`, pluralized — none of them join the
   * persisted roster; they only widen the allow-list `recipients_selected` is
   * validated against. Merged with `recipient_extra` when both are present,
   * de-duped case-insensitively. Each entry is held to the same shape check
   * (`400 invalid_recipient_extra`). Ignored under `recipient_email_override`.
   */
  recipients_extra?: string[];
  /**
   * Preview-composer: the per-send CC list (client request 2026-08-06) — the
   * composer's Cc field, directly under To and behaving like any email
   * client's.
   *
   * Same one-off contract as `recipients_extra`: none of these join the
   * persisted roster, and none of them widen the `recipients_selected`
   * allow-list — a CC address is not a To address. Each entry is held to the
   * same shape check (`400 invalid_recipient_cc`), so one malformed entry
   * refuses the whole send rather than being dropped.
   *
   * DEDUPE: an address that is also in the resolved To list is dropped from
   * CC — To wins, the way every mail client resolves it, so nobody is
   * addressed twice. Ignored entirely under `recipient_email_override`, which
   * redirects the whole package and must not leak a copy to the real people.
   *
   * The PM's own copy (Reply-To's address, CC'd so the PM keeps one) is server
   * business and is added on top of this list; it is not sent here.
   */
  recipients_cc?: string[];
  /**
   * Verification acknowledgments — the ack keys the PM ticked in the send
   * window's verification panel.
   *
   * The ONLY client input the send-time self-audit reads, and it is one-way:
   * an `ack_required` check whose key is absent REFUSES the send
   * (`428 verification_ack_required`), and nothing in this array can turn a
   * failed check into a passed one or move a single number. The checks are
   * recomputed server-side from the database and the rendered workbook either
   * way. A malformed value is dropped, which fails safe.
   */
  acks?: string[];
  /**
   * Close the sent rollup's owning submission after a successful send —
   * "Approve & Send is what closes a package" (blessed model, 2026-07-29).
   * Offered on the LAST unsent week of ANY open submission, default or not:
   * there is no is-default gating on this path.
   *
   * Runs AFTER the send commits and is deliberately non-fatal — the email has
   * already gone, so a close failure reports itself via `submission_closed` /
   * `submission_close_error` rather than failing the request. Ignored on
   * `dry_run`. If the closed submission was the PCO's default target, the PCO
   * is left with NO default; that is fine and self-healing — the next saved
   * block opens one via `getOrCreateDefaultSubmission`.
   */
  close_submission?: boolean;
  /**
   * Legacy composer field, kept for compatibility (the composer stopped
   * sending it 2026-07-24 in favor of `body_override`): a custom note
   * inserted into the cover letter above the Summary block. Plain in the
   * text body, HTML-escaped in the HTML body. Trimmed + capped at
   * `MAX_CUSTOM_MESSAGE_LEN` server-side; absent/blank renders
   * byte-identically to the template with no slot at all.
   */
  custom_message?: string;
  /**
   * Composer-edited plain-text body for THIS send (2026-07-24). When present
   * (non-blank after server sanitize), it replaces the rendered cover letter
   * verbatim as the text body, and the HTML body becomes the same text
   * (escaped, newlines → <br/>) in the standard email shell. Subject and
   * attachments are never overridable. Omit when the PM didn't edit the
   * prefilled body — the server then renders the letter exactly as before.
   * Trimmed, control-chars stripped, capped at `MAX_BODY_OVERRIDE_LEN`
   * server-side.
   */
  body_override?: string;
  /**
   * Composer-edited subject line for THIS send (2026-08-02). Mirrors
   * `body_override` exactly: present (non-blank after server sanitize) → it is
   * the email's Subject header VERBATIM; omitted → the server renders the
   * canonical subject itself, byte-identical to the pre-composer email.
   *
   * VERBATIM MEANS THE WHOLE LINE. The server does NOT re-apply the "REVISED: "
   * prefix or the "- MOD nnn" suffix on top of an override — the composer
   * prefills the field with the fully-rendered subject, so whatever the PM
   * leaves there is what he read and approved. Re-decorating it would let a PM
   * who deliberately deleted the prefix watch it come back.
   *
   * Control characters collapse to spaces, whitespace runs collapse to one,
   * trimmed, then capped at `MAX_SUBJECT_OVERRIDE_LEN` server-side. Blank after
   * that is treated as absent.
   */
  subject_override?: string;
}

/**
 * Read-only render of what an Approve & Send would actually email — powers the
 * preview-composer's subject/body pane + attachment list. Built by reusing the
 * SAME `renderCoverLetter` the real send uses, so the preview can never drift
 * from the sent email. No Resend send, no DB writes, no R2 reads.
 */
export interface RollupPreviewResponse {
  /** The canonically-rendered subject — includes the MOD suffix + REVISED:
   *  prefix. The composer PREFILLS its editable subject input from this; an
   *  untouched field omits `subject_override` and the server renders this
   *  exact line again at send time. */
  subject: string;
  /** Plain-text cover letter (what the composer renders in its preview pane). */
  text_body: string;
  /** HTML cover letter — same content, for parity with the sent email. */
  html_body: string;
  /** Default recipient set for this send (before the PM edits the checkboxes). */
  recipients: {
    primary: string;
    /** Roster entries (de-duped against the primary), in stored order. */
    additional: string[];
  };
  /** The files that would ride along, in send order (xlsx, then PDFs, then
   *  vendor invoices — matches submitRollup's allAttachments order). */
  attachments: Array<{
    filename: string;
    kind: 'xlsx' | 'pdf' | 'invoice';
    /**
     * Daily-PDF regeneration plan for this attachment (`kind: 'pdf'` only).
     * Absent on the xlsx and on vendor invoices, which are never regenerated.
     *
     * The filename is IDENTICAL either way — one page per daily, the
     * regenerated copy INSTEAD OF the original — so this is the only thing that
     * says which one the GC is getting.
     */
    regeneration?: RollupAttachmentRegeneration;
  }>;
  /** True when a prior send exists — the preview subject/body are REVISED. */
  is_revision: boolean;
  /**
   * The send's own self-audit, run for DISPLAY here and re-run server-side in
   * `submitRollup` before anything is stored, claimed, or sent. The composer
   * renders it as the verification panel and gates its Send button on it.
   *
   * Optional on the wire so a page that briefly outruns its Worker (Pages and
   * the Worker deploy from one push but not one instant) renders exactly as it
   * did before this feature rather than breaking. The GATE is the server's; a
   * client that cannot see the panel still cannot send past a failure.
   */
  verification?: SendVerification;
}

/**
 * What the send will attach for one foreman daily, and why.
 *
 * A daily is regenerated when ANY of three things is true: the PM corrected a
 * row on it (`change_count`), it also carries work outside this rollup that
 * must be scoped out (`scoped` / `omitted_block_count`), or a block has to be
 * reprinted under the PCO's canonical activity code
 * (`normalized_block_count`).
 *
 * THE GATE: a regenerated daily must carry the foreman's signature. When
 * `signature_status` is anything but `'captured'`, `plan` falls back to
 * `'original'` and `signature_blocked` is true — the composer must show that,
 * because it is the one case where the GC gets an uncorrected page and nothing
 * else on screen would say so. The server enforces the identical rule; this is
 * disclosure, never instruction.
 */
export interface RollupAttachmentRegeneration {
  /** What actually gets attached. */
  plan: 'original' | 'regenerated';
  /** PM-visible row changes on this daily's in-scope blocks. */
  change_count: number;
  /** Blocks dropped because they belong to another PCO / week / rollup. */
  omitted_block_count: number;
  /** In-scope blocks reprinted under the PCO's canonical activity code. */
  normalized_block_count: number;
  /** True when foreign work was scoped out (blocks omitted, or another job on the same file). */
  scoped: boolean;
  /** `'unknown'` = never attempted and not resolved here (dry-run path only). */
  signature_status: 'captured' | 'missing' | 'error' | 'unknown';
  /** True when regeneration was warranted but the signature refused it. */
  signature_blocked: boolean;
  /**
   * The job number this daily's page PRINTS, when the block was moved here from
   * another job and that disagrees with this rollup's project. Null on every
   * ordinary daily; absent on an API older than 2026-08-03.
   *
   * ON THE COMPOSER THIS IS THE MOST CONSEQUENTIAL FIELD IN THE OBJECT. A
   * transferred daily regenerates so the page carries THIS job's banner — but a
   * missing signature demotes it to the original, and the original is headed
   * with the OTHER job's number. That page goes to the GC attached to this
   * job's invoice.
   */
  transferred_from_job?: string | null;
}

// ---------------------------------------------------------------------------
// GENERATED DOCUMENTS — reading back what we actually sent the GC
// ---------------------------------------------------------------------------

/**
 * What the PCO Sources tab knows about a contributing daily's REGENERATED copy.
 *
 *   'generated' — a stored `generated_documents` artifact exists for this daily
 *                 on one of this PCO's rollups. It can be opened.
 *   'original'  — no artifact, but the daily rode along on a send of a rollup it
 *                 belongs to: Hoffman got the foreman's untouched page.
 *   'none'      — the daily has never been part of a send of this PCO.
 */
export type PcoGeneratedDocumentState = 'generated' | 'original' | 'none';

/**
 * One stored regenerated artifact, as the Sources tab reads it back.
 *
 * `regeneration` carries the SAME shape the send composer renders its badges
 * from, so both surfaces speak one vocabulary. It describes the CURRENT plan for
 * (this daily, this rollup) — which equals the plan the artifact was made under
 * for as long as the rollup stays locked, and a sent rollup is locked until
 * someone reopens it. `plan` is therefore always `'regenerated'` and
 * `signature_blocked` always false: a blocked daily produces no artifact at all.
 */
export interface PcoGeneratedDocumentArtifact {
  /** `generated_documents.id` — what the signed-URL route takes. */
  id: string;
  weekly_rollup_id: string;
  week_ending_date: string; // 'YYYY-MM-DD'
  /** ISO. The "latest artifact wins" ordering key. */
  created_at: string;
  page_count: number;
  generator_version: number;
  content_sha256: string;
  regeneration: RollupAttachmentRegeneration;
}

export interface PcoGeneratedDocument {
  daily_pdf_id: string;
  state: PcoGeneratedDocumentState;
  /** Non-null exactly when `state === 'generated'`. */
  artifact: PcoGeneratedDocumentArtifact | null;
}

/**
 * Sidecar to `PcoCumulativeResponse.contributing_pdfs` — same PCO, one row per
 * contributing daily that has something to say. Deliberately a separate call:
 * the cumulative response is the money surface and is fetched on every PCO
 * page load, while this is only read on the Sources tab.
 *
 * Dailies with nothing to report (`state: 'none'`) are omitted, so an absent
 * `daily_pdf_id` and an explicit `'none'` mean the same thing to the caller.
 */
export interface PcoGeneratedDocumentsResponse {
  documents: PcoGeneratedDocument[];
}

export interface SubmitRollupRequestPreview {
  /** The composer's current custom message — the preview re-renders with it so
   *  the body pane always shows exactly what will send. */
  custom_message?: string;
}

// ---------------------------------------------------------------------------
// SEND VERIFICATION — the receipt every Approve & Send produces
// ---------------------------------------------------------------------------

/**
 * Stable identifiers for the Tier-1 checks. Wire constants, so the composer can
 * key UI off them and a receipt written today is still readable when the copy
 * changes. NEVER renumber or reuse an id: a stored receipt refers to these.
 *
 * `'expected_manual_adds'` was retired 2026-08-13 (client ruling — the
 * hand-entered-work acknowledgment was removed outright). It is deliberately
 * NOT reused, and readers of a stored receipt must tolerate it: rows written
 * before that date still carry the id, and so does a response from a Worker
 * that has not yet redeployed. Anything keying off this union does so
 * defensively — an unrecognised id renders as an ordinary line, never a crash.
 */
export type VerificationCheckId =
  | 'workbook_readback'
  | 'ladder_identity'
  | 'provenance'
  | 'attachments'
  | 'revision_rollforward'
  | 'materials_enumerated'
  | 'independent_reread';

/**
 * Four outcomes, and the distinction between the last two is the whole
 * human-in-the-loop design:
 *
 *  - `ok`             — the check ran and agreed.
 *  - `fail`           — the check ran and DISAGREED. Blocks the send.
 *  - `ack_required`   — the check found something only a human can settle
 *                       (a material line no page can confirm; an independent
 *                       re-read that disagreed). Blocks Send until the PM ticks
 *                       it, never decides for him.
 *  - `not_applicable` — there was nothing to check (a first send has no prior
 *                       snapshot to roll forward from). Deliberately NOT `ok`:
 *                       a green line that means "we didn't look" is the exact
 *                       thing a verification panel must never print.
 */
export type VerificationCheckStatus = 'ok' | 'fail' | 'ack_required' | 'not_applicable';

export interface VerificationCheckLine {
  check: VerificationCheckId;
  status: VerificationCheckStatus;
  /** Short label for the panel row, e.g. "Workbook read-back". */
  label: string;
  /** One sentence with the REAL numbers in it. Never a generic reassurance. */
  detail: string;
  /**
   * Extra lines the panel shows under the detail — per-component figures on the
   * read-back, the specific offending rows on a provenance failure.
   */
  lines?: string[];
  /**
   * Present only on `ack_required`. `key` is what the client sends back in
   * `acks`; `copy` is the exact sentence the PM is agreeing to, and it is what
   * gets stored on the receipt.
   */
  ack?: { key: string; copy: string };
}

export interface VerificationTier2Summary {
  /** Contributing dailies on this rollup. */
  total: number;
  /** Re-read by an independent model and found to agree — INCLUDING the ones a
   *  human overrode (see `hand_checked`). */
  verified: number;
  /**
   * How many of `verified` got there because a HUMAN dismissed the flag in the
   * PDF Inbox ("Checked against the PDF — parse is right"), not because the
   * comparator agreed.
   *
   * Split out because the receipt is stored verbatim and read back months
   * later: without it, a week where the PM overrode two disagreements would
   * record "every name, ID and hour matches what was recorded", which is a
   * claim about the machine that a human decision had already replaced. The
   * override belongs in the record, permanently.
   */
  hand_checked: number;
  /** Re-read and DISAGREED — the PM has to acknowledge these. */
  flagged: number;
  /** Queued or not yet reached by the runner. */
  pending: number;
  /** The re-read itself failed (provider error, missing bytes). */
  errored: number;
  /** Never attempted — rows predating the feature. */
  unchecked: number;
  /** Filenames/dates of the flagged dailies, for the panel's "view details". */
  flagged_dailies: Array<{
    daily_pdf_id: string;
    report_date: string;
    foreman_name: string | null;
    summary: string;
  }>;
}

/**
 * The whole verification result for one rollup, as the composer renders it and
 * as `send_verifications` stores it.
 */
export interface SendVerification {
  /** Bumped when the CHECKS change, so an old receipt is never read as new. */
  verifier_version: number;
  /** Ordered exactly as the panel shows them. */
  checks: VerificationCheckLine[];
  tier2: VerificationTier2Summary;
  /** True when nothing failed — the "math checks out" state. */
  passed: boolean;
  /** Ack keys that must come back on the submit before Send is permitted. */
  required_acks: Array<{ key: string; copy: string }>;
}

export interface SubmitRollupResponse {
  /** ULID of the persisted submissions row, or null on dry_run. */
  submission_id: string | null;
  rollup_id: string;
  /** Every address the email actually went to (gc_email + additional
   *  recipients), comma-joined — display-only summary, not a parseable list. */
  recipient_email: string;
  /**
   * The same set as a real list, in send order (2026-08-02). Added because the
   * contact book has to know exactly who an email reached and
   * `recipient_email` is explicitly not parseable. Empty on `dry_run` — nothing
   * was delivered, so nothing is remembered.
   */
  recipients: string[];
  /**
   * The CC header as actually sent, comma-joined (2026-08-06) — the composer's
   * per-send Cc list (minus anything already in To) plus the PM's own copy.
   * Display-only, same convention as `recipient_email`; `''` when the send
   * carried no CC at all. Populated on `dry_run` too, because it is a
   * statement about the email that WOULD go out.
   */
  cc_email: string;
  /**
   * The same set as a real list (2026-08-06). Empty on `dry_run` for the same
   * reason `recipients` is: the contact book reads this field, and nothing was
   * delivered.
   */
  recipients_cc: string[];
  /** The Subject header as actually sent — the rendered line, or the PM's
   *  `subject_override` verbatim when he edited it. */
  subject: string;
  attached_pdf_count: number;
  /** R2 key for the persisted xlsx. null on dry_run. */
  xlsx_r2_key: string | null;
  /** Resend message id. null on dry_run. */
  resend_message_id: string | null;
  /** True for dry_run; false when actually sent. */
  would_send: boolean;
  invoice_total_cents: number;
  /**
   * Outcome of the optional `close_submission` step. Absent when the flag
   * wasn't sent (or on dry_run); `true` when the owning submission closed;
   * `false` when the close was attempted and refused — see
   * `submission_close_error`. The send itself succeeded either way.
   */
  submission_closed?: boolean;
  /**
   * Why the post-send close was refused, as a `SubmissionErrorCode` — most
   * usefully `unsent_weeks_exist` (the submission still has another unsent
   * week, so it isn't finished) or `not_open`. Null/absent on success.
   */
  submission_close_error?: string | null;
}

// ----- Send contact book -----
//
// Addresses this project has actually emailed, remembered so the composer's
// To field can suggest them next time. It is a MEMORY, not a roster: nothing
// here is configuration, nothing here is validated at read time, and nothing
// here is ever pre-selected for a send. The configured roster
// (`projects.gc_additional_emails`) remains the only thing that gets chipped
// automatically.

/** One remembered recipient of this project's sends. */
export interface SendContact {
  /** Stored lowercased — the composer matches case-insensitively anyway. */
  email: string;
  /** ISO timestamp of the most recent send that reached this address. */
  last_used_at: string;
  /** How many sends have reached it. Suggestion ordering is recency, not this;
   *  the count is here because it is the honest answer to "do I know this
   *  address or did it appear once by accident". */
  use_count: number;
}

/**
 * Whole contact book for the active project, most-recently-used first. There is
 * no server-side search and no pagination on purpose — this is dozens of rows,
 * the client already has to filter as the PM types, and a round trip per
 * keystroke would be worse than the payload.
 */
export interface SendContactsResponse {
  contacts: SendContact[];
}

// ----- PCO cumulative breakdown -----
//
// One server-side aggregation across all weeks of a PCO's activity. Powers
// the PCO command center page (PcoDetailPage). Aggregates labor by employee,
// equipment by asset, materials by item, then sums to invoice totals using
// the project's current markup_pct and per_diem_rate_cents.

export interface PcoCumulativeLaborLine {
  employee_id: string | null;
  /** HeavyJob ID — primary identifier surfaced in the UI ahead of name. */
  heavyjob_employee_id: string | null;
  employee_name: string;
  classification: string;
  total_st_hours: number;
  total_ot_hours: number;
  total_dt_hours: number;
  total_cents: number;
  per_diem_eligible: boolean;
  is_unknown: boolean;
}

export interface PcoCumulativeEquipmentLine {
  equipment_id: string | null;
  asset_id: string;
  description: string;
  total_hours: number;
  total_cents: number;
  is_unknown: boolean;
}

export interface PcoCumulativeMaterialLine {
  material_id: string | null;
  name: string;
  unit: string;
  total_quantity: number;
  total_cents: number;
}

/**
 * A subcontractor lump line inside a PCO's cumulative roll-forward. These are
 * rollup-level rows (`rollup_subcontractor_lines`) with no block or PDF behind
 * them, so unlike the labor/equipment/materials lines they don't aggregate —
 * each vendor invoice stays its own line and carries the week it billed in.
 */
export interface PcoCumulativeSubcontractorLine {
  id: string;
  description: string;
  /** Lump amount in integer cents (> 0). */
  amount_cents: number;
  /** 'YYYY-MM-DD' — the vendor invoice date. */
  invoice_date: string;
  /** Week-ending Saturday of the rollup that carries the line. */
  week_ending_date: string;
}

export interface PcoCumulativeTotals {
  labor_cents: number;
  equipment_cents: number;
  materials_cents: number;
  /** Subcontractor lump lines — in the subtotal, so in the markup base; never
   *  equipment-taxed, no per diem. Mirrors RollupTotals.subcontractors_cents. */
  subcontractors_cents: number;
  markup_cents: number;
  /** Equipment Usage Tax on the equipment subtotal only; OUTSIDE the markup
   *  base (a pass-through added alongside the markup). 0 when the project
   *  tax % is 0. */
  equipment_tax_cents: number;
  per_diem_cents: number;
  per_diem_hours: number;
  invoice_total_cents: number;
}

export interface PcoCumulativeContributingPdf {
  daily_pdf_id: string;
  report_date: string; // 'YYYY-MM-DD'
  foreman_name: string | null;
  email_from: string;
  email_subject: string;
}

export interface PcoCumulativeActivityCode {
  id: string;
  code: string;
  description_from_pdf: string | null;
}

export interface PcoCumulativeResponse {
  pco: {
    id: string;
    pco_number: string;
    description: string;
    status: PcoStatus;
  };
  /** Project-level rate snapshot at read time. */
  markup_pct: number;
  equipment_tax_pct: number;
  per_diem_rate_cents: number;
  /** Lines, sorted by total_cents descending. */
  labor: PcoCumulativeLaborLine[];
  equipment: PcoCumulativeEquipmentLine[];
  materials: PcoCumulativeMaterialLine[];
  /** Vendor-invoice lumps across every rollup of the PCO, biggest first. */
  subcontractors: PcoCumulativeSubcontractorLine[];
  totals: PcoCumulativeTotals;
  /**
   * Pre-portal billing for this PCO: SUM(imported weekly_rollups'
   * invoice_total_cents in billed statuses) + SUM(ledger-only non-open
   * submissions' imported_total_cents). Rendered as the Totals tab's
   * "Imported history" line: portal-era ladder (all saved work, open weeks
   * included) + this = all known work to date. Deliberately NOT the same
   * number as submitted-to-date, which excludes open weeks. 0 for PCOs with
   * no imported history.
   */
  imported_history_cents: number;
  weeks_with_activity: number;
  unknown_employee_count: number;
  unknown_equipment_count: number;
  contributing_pdfs: PcoCumulativeContributingPdf[];
  mapped_activity_codes: PcoCumulativeActivityCode[];
  /**
   * Whether labor renders/bills the ST/OT/DT premium split (true) or flat at
   * the ST rate (false). Mirror of !project.bill_overtime_as_st — the UI hides
   * the OT/DT columns and shows a single Total hrs column when false.
   */
  calculate_ot_dt: boolean;
}

// ----- PCO status PATCH -----

export interface PcoStatusPatchRequest {
  status: PcoStatus;
}

// ----- PCO attention aggregation -----
//
// Powers the NeedsAttention panel at the top of PcoDetailPage. Aggregates
// every category of "Joey needs to do something" scoped to one PCO into a
// single response so the panel renders without N follow-up fetches.

export interface PendingMaterialPreview {
  id: string;
  quoted_text: string;
  suggested_name: string | null;
  suggested_quantity: number | null;
  suggested_unit: string | null;
  daily_pdf_id: string;
  foreman_name: string | null;
  report_date: string; // 'YYYY-MM-DD'
}

export interface OpenVariancePreview {
  id: string;
  week_ending_date: string | null; // null when variance isn't tied to a week
  coffman_amount_cents: number;
  hoffman_amount_cents: number;
  variance_cents: number;
  status: VarianceStatus;
}

export interface StaleRollupPreview {
  rollup_id: string;
  week_ending_date: string;
  stale_reason: StaleReason | null;
  submitted_at: string | null;
}

export interface FailedPdfPreview {
  daily_pdf_id: string;
  received_at: string;
  foreman_name: string | null;
  parse_error: string | null;
}

/**
 * One unknown employee row surfaced in NeedsAttention. The HJ ID is the
 * canonical identifier (Coffman uses it to disambiguate same-named workers);
 * the name is the secondary display. Either may be null if the parser
 * couldn't pull both fields off the PDF.
 */
export interface UnknownEmployeeItem {
  heavyjob_employee_id: string | null;
  name: string;
}

/**
 * One unknown equipment row surfaced in NeedsAttention. The asset_id is the
 * canonical identifier; the description (often a model name like "745 HAUL
 * TRUCK -- WESTERN STAT") is shown smaller and muted because multiple assets
 * can share a description.
 */
export interface UnknownEquipmentItem {
  asset_id: string | null;
  description: string;
}

export interface PcoAttentionResponse {
  pco_id: string;
  pco_number: string;
  /**
   * @deprecated 2026-08-03 (M12) — served, never rendered. The NeedsAttention
   * panel has always excluded it, and the concept it counted (a material
   * candidate awaiting the PM's decision) no longer exists.
   */
  pending_materials: {
    count: number;
    items: PendingMaterialPreview[];
  };
  open_variances: {
    count: number;
    total_amount_cents: number;
    items: OpenVariancePreview[];
  };
  unknown_employees: {
    count: number;
    items: UnknownEmployeeItem[];
  };
  unknown_equipment: {
    count: number;
    items: UnknownEquipmentItem[];
  };
  stale_rollups: {
    count: number;
    items: StaleRollupPreview[];
  };
  failed_pdfs: {
    count: number;
    items: FailedPdfPreview[];
  };
}

// ----- PDF Import Inbox -----
//
// The post-2026-05-14 Dashboard primary surface. Every parsed PDF becomes a
// card; each card expands to one InboxBlock per activity-code section in the
// PDF. Joey reviews labor + equipment + materials per block, picks/confirms
// the PCO, and saves — committing confirmed_* values on the underlying
// daily_*_entries rows and flipping block_status from 'pending' to 'saved'.
//
// Only PDFs with at least one block_status='pending' appear in /api/inbox —
// plus a fully-processed daily whose independent re-read is still flagged over
// saved work (2026-08-03). Once every block on a PDF is decided the PDF drops
// off the inbox (its saved work lives on under /pcos for historical review).
// The one decision with no other surface is 'skipped', so those PDFs are
// reachable on request: `?include_skipped=1` → `skipped_pdfs` (2026-08-06).

export interface InboxPdf {
  daily_pdf_id: string;
  /** Joey-readable label derived via derivePdfFilename(). */
  filename: string;
  /** 'YYYY-MM-DD' — the work date the foreman reported. */
  report_date: string | null;
  foreman_name: string | null;
  email_subject: string;
  received_at: string; // ISO
  blocks_total: number;
  blocks_pending: number;
  blocks_saved: number;
  blocks_skipped: number;
  /**
   * Blocks whose activity code is on the IGNORE list. They are never work —
   * counted here only so the four statuses sum to `blocks_total` rather than
   * leaving an unexplained gap. A PDF whose blocks are ALL ignored has
   * `blocks_pending = 0` and therefore never appears in this list at all.
   */
  blocks_ignored: number;
  /**
   * @deprecated 2026-08-03 (M12) — served, never rendered. material_queue_items
   * in 'pending' across all blocks for this PDF. No longer a review signal:
   * parser material candidates are stored context, not a to-do.
   */
  materials_pending: number;
  /**
   * `'flagged'` when a second AI model re-read this report and DISAGREED with
   * what the parser stored. Every other verification state — verified, pending,
   * errored, never attempted — serializes as null on purpose: only a
   * disagreement is news, and a badge on every card would bury the one that
   * matters. Absent on an API older than 2026-08-02.
   *
   * ADVISORY ONLY. It blocks nothing here and nothing downstream; the send
   * composer asks the PM to acknowledge it, and that is the entire consequence.
   */
  verify_status?: 'flagged' | null;
  /** One-line summary of the disagreement, for the card's amber note. */
  verify_summary?: string | null;
}

/**
 * One PCO represented among the inbox's PENDING blocks, with how many there
 * are. Drives the list's filter row ("All (40) · 002 (19) · 004 (12)").
 * "Represented" uses the same rule the block card shows: the block's chosen
 * `pco_id`, else its activity code's mapping. Blocks with no PCO at all are
 * not faceted — there is nothing to filter by.
 */
/**
 * Sentinel `pco_id` for the inbox filter meaning "pending blocks with no PCO
 * at all" — nothing chosen AND an unmapped activity code. It and the real PCO
 * facets TILE EXACTLY: every pending block is in one bucket or the other, so
 * the counts sum to the total.
 */
export const INBOX_UNASSIGNED_PCO = 'unassigned';

/** One foreman represented among the inbox's pending blocks. */
export interface InboxForemanFacet {
  foreman_name: string;
  pending_block_count: number;
}

export interface InboxPcoFacet {
  pco_id: string;
  pco_number: string;
  description: string;
  pending_block_count: number;
}

export interface InboxListResponse {
  pdfs: InboxPdf[];
  /**
   * Every PCO with pending work, biggest pile first. Absent on an API older
   * than 2026-07-31 — the UI renders no filter row rather than an empty one.
   *
   * CROSS-FILTERED: computed with the FOREMAN filter applied but its own
   * dimension unfiltered, so each count answers "if I pick this PCO, how much
   * under the foreman I already chose". They therefore change per request —
   * read them from every response rather than caching.
   */
  pco_facets?: InboxPcoFacet[];
  /** Pending blocks with no effective PCO (the `unassigned` bucket). */
  unassigned_count?: number;
  /** Foremen with pending work, cross-filtered by the PCO selection. */
  foreman_facets?: InboxForemanFacet[];
  /**
   * How many decided PDFs the work pile hides that still carry a SKIPPED
   * block — the reveal's count, served on every response so the UI can offer
   * it without asking for the rows. 0 whenever a PCO/foreman filter is
   * applied: both filters are defined over pending blocks, which these PDFs
   * have none of, so a count under a filter would promise rows the filter
   * cannot deliver. Absent on an API older than 2026-08-06 — the UI renders
   * no affordance rather than a "0 skipped reports" line.
   */
  skipped_count?: number;
  /**
   * The rows behind that count. Present ONLY when the request asked
   * (`?include_skipped=1`); `pdfs` never carries them, so its meaning ("work
   * left") is unchanged for every existing caller.
   */
  skipped_pdfs?: InboxPdf[];
}

export interface InboxLaborRow {
  daily_labor_entry_id: string;
  employee_id: string | null;
  /** HeavyJob ID — primary identifier surfaced in the UI ahead of the name. */
  hj_id: string | null;
  name: string;
  /**
   * Exactly what the foreman's PDF said — the immutable audit of the
   * submitted line. When the line is FK-linked to a roster employee whose
   * name differs (a generic craft placeholder like "LABORER-ID11" reassigned
   * to a real person), the UI shows this as a muted "PDF: …" hint.
   */
  employee_name_raw: string;
  /** Raw HeavyJob ID captured from the PDF row; null on legacy rows. */
  employee_hj_id_raw: string | null;
  /**
   * What the report's ID column printed, when it is NOT the id this line now
   * bills as — set only on a line that HAS a roster employee whose HeavyJob id
   * differs from `employee_hj_id_raw` (trimmed, case-insensitive).
   *
   * Drives the muted "PDF says LABORER-ID11" marker beside the name, and it is
   * the SAME marker for a hand reassign and for a remembered crew slot intake
   * filled on its own: a repeated decision must never be less visible than a
   * fresh one. Derived from the row, so nothing is stored for it and `*_raw`
   * stays the untouched audit of what the foreman submitted.
   *
   * Absent on an API older than 2026-09-21 — the card renders no marker rather
   * than guessing.
   */
  printed_id?: string;
  classification: string;
  parsed_st_hours: number;
  parsed_ot_hours: number;
  parsed_dt_hours: number;
  /** st + ot + dt, computed server-side for convenience. */
  parsed_total_hours: number;
  confirmed_total_hours: number | null;
  /** Roster's current ST rate (cents); 0 when the employee FK is null. */
  default_rate_cents: number;
  confirmed_rate_cents: number | null;
  per_diem_eligible: boolean;
  is_unknown: boolean;
  is_manual: boolean;
  /** Shift billed for this line; selects day vs swing rate. */
  daypart: Daypart;
  /** The employee's day rate (roster st_rate_cents). */
  day_rate_cents: number;
  /** The employee's swing rate; null if not swing-rated. */
  swing_rate_cents: number | null;
  /** The employee's typical shift, used as this line's default. */
  primary_daypart: Daypart;
  /**
   * The project's Sunday-premium rate WHEN this block's report_date is a Sunday
   * and the premium is configured; null otherwise (which is every ordinary
   * block). Non-null means "this row's base rate is this, whatever its daypart
   * says" — the client's rateForDaypart honors it exactly as billing.ts's
   * laborBaseRateCents does server-side.
   *
   * It has to ride on the ROW rather than be derived client-side because the
   * save stamps `confirmed_rate_cents` from what the PM sees; if the card
   * showed the weekday rate the pair would freeze there and the premium — which
   * only reaches the fallback tier — could never apply to that block again.
   */
  sunday_premium_rate_cents: number | null;
}

export interface InboxEquipmentRow {
  daily_equipment_entry_id: string;
  equipment_id: string | null;
  asset_id: string;
  description: string;
  /**
   * Exactly what the foreman's PDF said — the immutable audit of the
   * submitted line (rate-book categories like "FLEET TRUCKS" when the
   * foreman doesn't log a real asset). Shown as a muted "PDF: …" hint when
   * the FK-linked roster description differs.
   */
  equipment_name_raw: string;
  /** Raw asset id captured from the PDF row; null when the PDF had none. */
  asset_id_raw: string | null;
  parsed_hours: number;
  confirmed_hours: number | null;
  /** Roster's current hourly rate (cents); 0 when the equipment FK is null. */
  default_rate_cents: number;
  confirmed_rate_cents: number | null;
  is_unknown: boolean;
  is_manual: boolean;
}

export interface InboxMaterialRow {
  material_queue_item_id: string;
  status: MaterialQueueStatus;
  quoted_text: string;
  /** The parser's best-guess match against materials_master, if any. */
  suggested_material_id: string | null;
  suggested_name: string | null;
  suggested_unit: string | null;
  suggested_quantity: number | null;
  /**
   * Default rate (cents) from the suggested material's roster row. 0 when no
   * suggested_material_id or the master row was deleted. Used as the
   * pre-fill for the inline Rate input on the inbox card.
   */
  suggested_default_rate_cents: number;
  confirmed_material_id: string | null;
  confirmed_quantity: number | null;
  confirmed_rate_cents: number | null;
  is_manual: boolean;
}

/**
 * Pointer at another block that looks like the SAME daily report: same
 * foreman, same work date, same activity code — but a different source PDF.
 *
 * Inbound de-dupe is byte-level (`(project_id, content_hash)` unique), so a
 * re-signed / re-exported copy of a daily that's already in the system has
 * different bytes, parses cleanly, and lands as a second PDF carrying twin
 * pending blocks. Saving both bills the day twice.
 *
 * Advisory only. A legitimate second report for the same code on the same day
 * exists (split crews filing separately), so this never blocks a Save — the PM
 * decides.
 */
export interface InboxDuplicateRef {
  /**
   * The look-alike BLOCK itself. Needed to address it — "Replace older block"
   * (2026-07-31) posts this as `replaces_daily_entry_id`. Optional on the wire
   * so a response from an API older than the field still parses; the web hides
   * the replace action when it is absent rather than guessing.
   */
  daily_entry_id?: string;
  /** The OTHER PDF — the one already carrying the look-alike block. */
  daily_pdf_id: string;
  /** Joey-readable label for that PDF (derivePdfFilename), for the warning copy. */
  filename: string;
  /** 'pending' or 'saved'. A skipped sibling never flags (it bills nothing). */
  block_status: BlockStatus;
}

/**
 * ONE ROW THE INDEPENDENT RE-READ DISAGREED ABOUT, shaped for display.
 *
 * Derived server-side from `daily_pdfs.verify_json` — the raw comparator blob
 * never reaches the client. Only NON-MATCH verdicts appear, and only the ones
 * that actually made the daily flag: a row both sides show at zero hours can
 * never move money and is the comparator's largest source of identity noise, so
 * it is excluded here for the same reason it is excluded from `agrees`.
 */
export interface InboxVerifyRow {
  section: 'labor' | 'equipment';
  /** The activity code the row sits under, as the page printed it. */
  activity_code: string | null;
  /** "Walter Fenwick — 3.0h" — who/what, and the hours in question. */
  label: string;
  /**
   * `missing_from_page` — we have the row, the re-read could not find it.
   * `hours_differ`      — both sides have the row, the hours disagree.
   * `extra_on_page`     — the re-read saw a row we have no record of.
   */
  verdict_kind: 'missing_from_page' | 'hours_differ' | 'extra_on_page';
  /**
   * Our side of the row, in PM English. Null when we recorded nothing.
   *
   * On `missing_from_page` it reads "not found on the page" rather than
   * echoing the hours: the card renders `label — stored_summary`, and the label
   * already carries the recorded hours, so an echo printed as "… — 10.0h —
   * recorded 10.0h" directly under a headline saying the row could not be
   * found, which reads as a contradiction.
   */
  stored_summary: string | null;
  /** What the second read saw. Null when the page had nothing. */
  read_summary: string | null;
}

/**
 * The disagreement attached to ONE block — what the amber strip on that block's
 * card renders. Present only on blocks that actually carry a disagreement;
 * blocks whose rows all matched get `null` and render nothing.
 */
export interface InboxVerifyBlockFlag {
  /** Ready-to-render sentence, verdict-aware. */
  headline: string;
  /**
   * True when the re-read found no section for this activity code AT ALL — the
   * rows list is then empty and the headline is the whole story.
   */
  section_missing: boolean;
  rows: InboxVerifyRow[];
}

/** A human saying "I checked this against the PDF and the parse is right." */
export interface InboxVerifyAck {
  /** ISO timestamp. */
  at: string;
  by_user_id: string | null;
  /** Machine key for what was confirmed — `'confirmed_against_pdf'` today. */
  note: string;
}

/**
 * Per-daily state of the Tier-2 independent re-read, for the detail pane header.
 *
 * ADVISORY ONLY, exactly as everywhere else: nothing here gates a save or a
 * send. `'flagged'` is the only state that renders anything loud; the rest are
 * carried so the pane can show the quiet "already checked" receipt after a
 * dismiss, and so a client can tell "verified" from "never re-read".
 */
export interface InboxVerifyDetail {
  status: 'flagged' | 'verified' | 'pending_verify' | 'error' | 'unchecked';
  /** The daily-level sentence under the header. Null unless flagged. */
  headline: string | null;
  /** Same one-liner the inbox list's amber tag carries. Null unless flagged. */
  summary: string | null;
  /** Activity codes of the blocks carrying a disagreement, in display order. */
  flagged_block_codes: string[];
  /** Sections the re-read saw that this parse produced no block for. */
  unmatched_read_blocks: string[];
  /** Set once a human dismissed the flag. Survives the flip to 'verified'. */
  acknowledged: InboxVerifyAck | null;
  /** Whether the dismiss action applies right now (i.e. status is 'flagged'). */
  can_acknowledge: boolean;
}

export interface InboxBlock {
  daily_entry_id: string;
  activity_code: string;
  code_description: string | null;
  quantity_label: string | null;
  block_status: BlockStatus;
  notes: string;
  /**
   * Default PCO suggestion derived from activity_codes.mapped_pco_id. Null
   * when this is the first time the parser has seen this code.
   */
  suggested_pco_id: string | null;
  /**
   * Current pco_id on the daily_entries row — what the block is actually
   * routed to right now. Pre-save: typically matches suggested_pco_id (the
   * parser pre-fills via the activity_code mapping); null if the code was
   * unmapped at parse time. Post-save: whatever Joey picked.
   */
  current_pco_id: string | null;
  /**
   * True when this block's work has already been billed — its created_at falls
   * on/before the most recent submitted/approved/paid rollup for its PCO.
   * Billed blocks can't be reopened (the reopen endpoint returns 409
   * block_is_billed); moving billed work is the post-trial reassignment flow.
   */
  is_billed: boolean;
  /**
   * Near-duplicate tripwire. Non-null only on a PENDING block that has a
   * non-skipped twin (same foreman + report_date + activity_code) on a
   * DIFFERENT PDF in the same project. Drives a warning banner on the block
   * card; Save stays enabled. See InboxDuplicateRef.
   */
  possible_duplicate_of: InboxDuplicateRef | null;
  /**
   * What the independent re-read disagreed about ON THIS BLOCK. Null — the
   * common case — when it agreed, when the daily was never re-read, or when the
   * disagreement lives on a different block of the same PDF. Absent on an API
   * older than 2026-08-03.
   *
   * ADVISORY: Save is untouched, and a save never clears it. Only the explicit
   * dismiss does (POST /api/inbox/pdfs/:id/verify-ack).
   */
  verify_flag?: InboxVerifyBlockFlag | null;
  /**
   * Set when this block was moved here from ANOTHER JOB by a person — the
   * printed page still says a different job number and (usually) a different
   * activity code. Drives the muted provenance note on the card. Null on every
   * ordinary block; absent on an API older than 2026-08-03.
   */
  transferred_from?: InboxBlockTransferOrigin | null;
  /**
   * The foreman who filed the block's source PDF, but ONLY when the report
   * carried a HeavyJob foreman id — the stable key a remembered crew-slot
   * decision is filed under.
   *
   * Non-null is therefore the UI's test for "can this line's slot be
   * remembered": null means there is no key, so the reassign dialog offers no
   * Remember checkbox rather than letting the PM tick a box the server would
   * refuse. Null on a manual block (no page) and on any report the parser read
   * no foreman id off. Absent on an API older than 2026-09-21.
   */
  foreman?: InboxBlockForeman | null;
  labor: InboxLaborRow[];
  equipment: InboxEquipmentRow[];
  materials: InboxMaterialRow[];
}

/** The block's source report's foreman, keyed by HeavyJob id. */
export interface InboxBlockForeman {
  hj_id: string;
  /** As printed on the report; null when the parser read no name. */
  name: string | null;
}

export interface InboxPdfDetailResponse {
  daily_pdf_id: string;
  filename: string;
  report_date: string | null;
  foreman_name: string | null;
  email_subject: string;
  blocks: InboxBlock[];
  /**
   * Tier-2 independent re-read state for this daily. Absent on an API older
   * than 2026-08-03 — the pane renders nothing rather than guessing.
   */
  verify?: InboxVerifyDetail | null;
}

/**
 * POST /api/inbox/pdfs/:dailyPdfId/verify-ack — "Checked against the PDF, the
 * parse is right." Flips `verify_status` 'flagged' → 'verified' and records the
 * ack INSIDE `verify_json`, so the list tag and the send composer's
 * acknowledgment both clear. 409 `not_flagged` when there is nothing to dismiss.
 */
export interface InboxVerifyAckResponse {
  daily_pdf_id: string;
  verify: InboxVerifyDetail;
}

/**
 * Body for POST /api/inbox/blocks/:dailyEntryId/save. Labor + equipment edits
 * land on the corresponding daily_labor_entries / daily_equipment_entries rows
 * as confirmed_* values. Any row Joey didn't touch still appears in the body
 * with confirmed values pre-populated from the parsed/default fallback chain —
 * the server treats both untouched and edited the same.
 */
export interface InboxBlockSaveRequest {
  pco_id: string;
  labor: Array<{
    daily_labor_entry_id: string;
    total_hours: number;
    rate_cents: number;
    /**
     * Chosen shift for this line (persisted; also selects the stamped rate).
     * Optional for backward compat — the pre-swing UI omits it and the server
     * defaults to 'day'. The swing-rate UI sends it explicitly.
     */
    daypart?: Daypart;
  }>;
  equipment: Array<{
    daily_equipment_entry_id: string;
    hours: number;
    rate_cents: number;
  }>;
  /**
   * Open the PCO's NEXT submission and save this block into it, instead of
   * letting the block land in whatever submission is currently the default
   * target. The new submission inherits MOD/FWA from the most recent prior
   * and becomes the default, so subsequent saves follow it; the old one stays
   * open awaiting close-out.
   *
   * **The UI no longer sends this as of 2026-07-29** — submission creation
   * moved to a dialog-first flow (`POST /api/pcos/:pcoId/submissions/start-next`,
   * then an ordinary save), which also lets the PM set the number and refs.
   * The flag stays functional for API callers and is still fully guarded; the
   * `week_already_collecting` check below protects this path specifically.
   *
   * Rejected with `409 week_already_collecting` when the block's (PCO, week)
   * already has ANY rollup — an ordinary save never splits a week, so the
   * block would be silently swallowed by the existing rollup instead of
   * starting anything. The 409 body names the owning submission and its
   * rollup status; the remedy depends on it — an in_progress week can move
   * whole via Transfer Week, a sent week is saved normally (flagging it for
   * revision) or Reopened first.
   *
   * Optional; absent/false is the ordinary save.
   */
  start_next_submission?: boolean;
}

/**
 * POST /api/inbox/blocks/:dailyEntryId/replace — "Replace older block".
 *
 * Josh re-sends a daily whose content CHANGED after the original was already
 * saved (and often already billed). The manual recipe was: reopen the old
 * block, skip it, save the new one. This is that recipe as one guided action.
 *
 * Body carries the block being retired. The pair must satisfy the same
 * near-duplicate relation the inbox banner flags (same report_date + activity
 * code + foreman, different PDFs) — the endpoint re-derives it rather than
 * trusting the caller.
 *
 * Order is the safety, because D1 has no transactions: every gate runs first,
 * then the new block saves (which recomputes, and auto-reopens the week if it
 * was already sent), then the old block is reopened and skipped. If a step
 * after the save fails, the response still returns 200 with `partial: true`
 * and `remaining_action` naming exactly what is left to do by hand — a
 * half-done replace must never look like a failure that changed nothing.
 *
 * Errors: `{ error }` — 'not_found' (404, either block outside the project);
 * 'block_not_pending' (409, the new block isn't pending); 'target_not_saved'
 * (409, the block being replaced isn't saved); 'not_a_duplicate' (409, the
 * pair doesn't satisfy the near-duplicate relation); plus every ordinary save
 * gate ('unrostered_entities' 409, 'zero_rate_with_hours' 409, 'invalid_pco'
 * 400). The 'materials_pending' 400 was removed 2026-08-03 (M12).
 */
export interface InboxBlockReplaceRequest {
  /** The SAVED block this one supersedes. */
  replaces_daily_entry_id: string;
}

export interface InboxBlockReplaceResponse {
  /** The newly saved block. */
  block: InboxBlock;
  /** The block that was retired, and how far the retirement got. */
  replaced: {
    daily_entry_id: string;
    /** 'skipped' on the happy path; 'saved' or 'pending' on a partial. */
    block_status: BlockStatus;
  };
  /** True when the save committed but the retirement did not fully complete. */
  partial: boolean;
  /** Plain-English next step when `partial` — else null. */
  remaining_action: string | null;
}

export interface InboxBlockResponse {
  block: InboxBlock;
  /**
   * Present only when the save opened a submission (`start_next_submission`).
   * Lets the UI name it in the confirmation toast without a refetch.
   */
  opened_submission?: {
    id: string;
    submission_number: string;
  };
}

/**
 * Bodies for the block-level manual line-add endpoints
 * (POST /api/inbox/blocks/:dailyEntryId/{labor,equipment,material}/add).
 * No report_date / week / PCO — all inherited from the block. reason optional.
 */
export interface InboxLaborAddRequest {
  employee_id: string; // roster FK required (quick-create rosters first)
  total_hours: number; // > 0
  rate_cents: number; // >= 0 integer
  reason?: string; // optional, <= 280 chars
}

export interface InboxEquipmentAddRequest {
  equipment_id: string; // roster FK required
  hours: number; // > 0
  rate_cents: number; // >= 0 integer
  reason?: string;
}

export interface InboxMaterialAddRequest {
  quoted_text: string; // free-text description (required)
  unit?: string;
  quantity: number; // > 0
  rate_cents: number; // per-unit, >= 0 integer
  material_id?: string | null; // optional master link for rate prefill
  reason?: string;
}

/**
 * Bodies for the line-reassign endpoints
 * (POST /api/inbox/blocks/:dailyEntryId/{labor,equipment}/:rowId/reassign).
 * Points a parsed line at an existing roster entry so ID/class/shift/rate all
 * flow from the roster — the correction for a foreman writing a generic craft
 * placeholder ("LABORER-ID11") instead of a real person/asset. `null` clears
 * the link back to the parsed identity. The `*_raw` columns are never touched;
 * they stay as the audit of what the foreman submitted. Pending blocks only.
 */
export interface InboxLaborReassignRequest {
  employee_id: string | null;
  /**
   * Remember this crew slot for this foreman on this job — the PM ticking
   * "Remember this for {foreman}'s reports" in the reassign dialog.
   *
   * Absent/false is the ordinary reassign and writes nothing but the line.
   * True stores the decision (`foreman_placeholders`), so every later daily
   * from that foreman links the same slot to the same person, and fills the
   * other PENDING blocks already waiting with the same slot. Refused before
   * any write when the target is `null` (`remember_requires_target`), the
   * report carries no foreman id (`foreman_unidentified`), or the line has no
   * printed id to key on (`placeholder_missing`).
   */
  remember?: boolean;
}

export interface InboxEquipmentReassignRequest {
  equipment_id: string | null;
}

/**
 * What a remembered reassign actually did, so the UI can say it without a
 * second request. Absent whenever `remember` was not asked for.
 */
export interface InboxPlaceholderRemembered {
  /** As printed on the report when the decision was made. */
  foreman_name: string;
  /** The slot id, trimmed — what the foreman's page printed in the ID column. */
  placeholder_id: string;
  /** The roster employee the slot now means. */
  employee_name: string;
  /**
   * How many OTHER labor lines this filled — still-unknown lines carrying the
   * same slot on this job's PENDING blocks. Pending only: those blocks are in
   * no rollup, so nothing recomputes and no money moves.
   */
  filled_other_lines: number;
  /** True when this REPLACED an earlier decision for the same slot. */
  replaced_previous: boolean;
}

/**
 * Response of the labor reassign endpoint. Structurally the block response
 * every other inbox mutation returns, plus the remembered-decision receipt.
 */
export interface InboxLaborReassignResponse extends InboxBlockResponse {
  remembered?: InboxPlaceholderRemembered | null;
  /**
   * The reassign landed but storing the decision did not. D1 has no
   * transactions and the line write commits first, so this cannot be a
   * rollback — the line really did move, and only the memory was lost. Ops has
   * been alerted; the UI has to say both halves, because "reassigned" alone
   * would leave the PM believing the portal will repeat it.
   */
  remember_failed?: boolean;
}

// ----- Move block -----
//
// One action that re-homes a saved block: change its PCO and/or its work date.
// The new date decides the week (report_date-in-week membership), so the
// destination rollup is fully determined by destination_pco_id + report_date.
// Allowed only when the source week AND destination week are open (in_progress)
// or the destination week has no rollup yet; a sent week must be reopened first.
export interface MoveBlockRequest {
  /** Destination PCO id (may equal the current PCO — a same-PCO week move). */
  destination_pco_id: string;
  /** New work date 'YYYY-MM-DD'. May equal the current date (a pure PCO move). */
  report_date: string;
  /** Required justification, 10–280 chars. */
  reason: string;
  /**
   * Optional submission to move INTO. Required by the API when the destination
   * (PCO, week) is split across submissions (design §3.3 rule 5); when present
   * the move SETS the pin to that submission's rollup instead of clearing it.
   * Errors: 'submission_required_for_split_week' /
   * 'invalid_destination_submission' — see SubmissionErrorCode.
   */
  destination_submission_id?: string | null;
}

export interface MoveBlockResponse {
  ok: true;
  daily_entry_id: string;
  source_pco_id: string;
  destination_pco_id: string;
  /** Saturday week-ending the block left / landed in (for nav/refetch). */
  source_week_ending: string;
  destination_week_ending: string;
  /** The submission the block was pinned into, when the move set a pin. */
  destination_submission_id?: string | null;
}

// ----- Assign blocks to a submission (carve-out mover) -----

/** Errors: `{ error: SubmissionErrorCode }` — 'submission_not_found' /
 *  'submission_not_open' / 'source_week_locked'. */
export interface AssignBlocksRequest {
  submission_id: string;
  block_ids: string[];
}

export interface AssignBlocksResponse {
  ok: true;
  submission_id: string;
  /** Saturday week-endings touched (for the client to refetch). */
  weeks: string[];
}

// ----- Cross-project block transfer -----
//
// The operation the PM does with a red pen: a foreman's daily prints one job's
// banner and code while the work is deliberately billed under another job.
// PENDING and SKIPPED blocks only — a SAVED block is in a rollup and has to be
// re-opened first (409 'block_is_saved'), which is where the billed-work and
// sent-week guards already live.
//
// Errors: 400 'destination_project_required' / 'destination_pco_required' /
// 'invalid_reason' / 'same_project' / 'invalid_destination';
// 403 'project_forbidden' (caller holds no membership on the destination, or
// the id isn't a project — one answer, so ids can't be probed);
// 409 'block_is_saved' / 'block_ignored' / 'block_not_transferable' /
// 'manual_block_not_transferable' / 'destination_code_ignored' /
// 'block_has_materials'.

export interface TransferBlockProjectRequest {
  /** Target project — must be one the CALLER holds a membership on. */
  destination_project_id: string;
  /** Target PCO, which must belong to `destination_project_id` and not be void. */
  destination_pco_id: string;
  /** Required justification, 10–280 chars. Rides the audit row. */
  reason: string;
}

/**
 * How many rows found an identity on the TARGET project's roster and how many
 * did not. Unmatched rows keep their raw PDF text, carry a NULL roster FK, and
 * render with the existing unknown badge at $0 — and the block save refuses
 * while one of them carries hours, so they cannot bill silently.
 */
export interface TransferBlockResolution {
  labor_total: number;
  labor_resolved: number;
  labor_unknown: number;
  equipment_total: number;
  equipment_resolved: number;
  equipment_unknown: number;
}

export interface TransferBlockProjectResponse {
  ok: true;
  daily_entry_id: string;
  destination_project_id: string;
  destination_pco_id: string;
  /** The target project's `daily_pdfs` row the block now hangs off. */
  destination_daily_pdf_id: string;
  /** True when no sibling row existed for these bytes and one was created. */
  minted_sibling: boolean;
  /** True when the target project had no row for the filed activity code. */
  created_activity_code: boolean;
  resolution: TransferBlockResolution;
}

/**
 * Provenance on a block that arrived by cross-project transfer. Derived, never
 * stored: the block's `daily_pdfs` row keeps the TRUTHFUL printed job number,
 * and a disagreement with the project it now lives on is the signal (every
 * ordinarily-parsed row agrees by construction). Null on every other block.
 */
export interface InboxBlockTransferOrigin {
  /** The job number the foreman's page actually prints, e.g. '25124'. */
  printed_job_number: string;
  /** The activity code as filed on that page, e.g. '954E--804'. Null if unknown. */
  filed_activity_code: string | null;
}

// ----- Active-PCOs Dashboard -----
//
// One response per Dashboard load. Each row drives one card in the rebuilt
// active-PCOs control panel. Sorted server-side: attention-needing first
// (descending), then PCOs with current-week activity, then by pco_number.

export interface ActivePcoCurrentWeek {
  week_ending_date: string;
  submission_number: string | null;
  status: RollupStatus;
  ready_to_send: boolean;
  subtotal_cents: number;
  days_with_entries: number;
  /** @deprecated 2026-08-03 (M12) — no longer a term in `ready_to_send`. */
  materials_pending_count: number;
  unknown_count: number;
  stale: boolean;
  stale_reason: StaleReason | null;
}

export interface ActivePcoAttentionSummary {
  /**
   * @deprecated 2026-08-03 (M12) — still counted, but excluded from the
   * endpoint's own `attention_count` total: nothing can act on it.
   */
  pending_materials: number;
  open_variances: number;
  /** unknown_employees + unknown_equipment summed. */
  unknown_total: number;
  stale_rollups: number;
  failed_pdfs: number;
}

export interface ActivePcoSummary {
  pco_id: string;
  pco_number: string;
  description: string;
  status: PcoStatus;
  /** null when no rollup row exists for this PCO + this week. */
  current_week: ActivePcoCurrentWeek | null;
  attention_summary: ActivePcoAttentionSummary;
  total_attention_count: number;
}

export interface ActivePcosResponse {
  active_pcos: ActivePcoSummary[];
  /** Saturday on or after today (project-wide context strip). */
  week_ending_date: string;
}

// ----- PDF preview (presigned R2 URLs) -----
//
// Returned from `GET /api/pdfs/:id/signed-url`. The `url` points directly at
// R2's S3-compatible endpoint with auth params in the query string. TTL is
// 15 minutes; UI re-calls the endpoint when the user wants to re-view.

export interface SignedPdfUrlResponse {
  /** Absolute https URL with presigned auth in the query string. */
  url: string;
  /** ISO-8601 absolute expiry timestamp (server time). */
  expires_at: string;
  /** Filename baked into the URL's Content-Disposition + suitable for UI labels. */
  filename: string;
}

// ----- Intake Health (Slice A reliability dashboard) -----
//
// Powers the "Today's Intake" card. Counts per parse_status over a rolling
// window, plus the recent rows Joey should actually look at (failed +
// parsed_with_warnings + stuck-pending).

export interface IntakeHealthCounts {
  pending: number;
  parsed: number;
  parsed_with_warnings: number;
  failed: number;
  total: number;
}

export interface IntakeHealthAttentionItem {
  daily_pdf_id: string;
  parse_status: ParseStatus;
  email_from: string;
  email_subject: string;
  received_at: string; // ISO
  parsed_at: string | null; // ISO
  report_date: string | null; // 'YYYY-MM-DD'
  foreman_name: string | null;
  parse_error: string | null;
  /** Parsed JSON array of soft-validation warnings. Empty if none. */
  parse_warnings: string[];
  parser_request_id: string | null;
  retry_count: number;
  /**
   * The PDF failed BEFORE routing, so it is still sitting in the provisional
   * intake project and belongs to no job yet. Such rows are shown to every PM
   * (see IntakeHealthResponse.attention) — one of them may be theirs, and the
   * alternative is a failure nobody can see. Optional on the wire; absent or
   * false means the row is genuinely in the viewer's project.
   */
  is_unrouted?: boolean;
}

export interface IntakeHealthResponse {
  /** Window the counts cover (inclusive of both endpoints). */
  window_days: number;
  /** PROJECT-SCOPED throughout — the health metric for this job. */
  counts: IntakeHealthCounts;
  /**
   * Recent items in failed / parsed_with_warnings / pending>10min states,
   * most recent first, capped to ~50 for the dashboard.
   *
   * RED MEANS "CONFIRMED FAILED, NEEDS A HUMAN" (client ruling 2026-08-12). A
   * failed row the auto-retry cron is still working — not a deterministic
   * failure, attempts left, and recently touched — is NOT here; it is counted
   * in `retrying_now` instead.
   *
   * NOT purely project-scoped, deliberately: inbound PDFs are inserted under
   * the provisional intake project and only re-homed to their real job ON
   * SUCCESS, so a failed parse never reaches the job it was for. Scoping this
   * list to the viewer's project therefore made every other job's failures
   * invisible to the person who sent them, while piling them into the default
   * project's PM's banner as PDFs he had never seen. Unrouted FAILED rows are
   * now surfaced to every PM, flagged `is_unrouted`.
   */
  attention: IntakeHealthAttentionItem[];
  /** Median + p95 latency in ms over the window. null if no parses. */
  latency_p50_ms: number | null;
  latency_p95_ms: number | null;
  /** Count of PDFs that retried at least once. */
  retried_count: number;
  /**
   * PDFs that have arrived and are still parsing right now — this project's
   * plus the unrouted ones (a PDF has no job until it parses, so a PM watching
   * for his own forward would otherwise see zero). Exists so a big batch is
   * distinguishable from "nothing was sent": for the first minutes of one,
   * every other signal on this response is still empty. Optional on the wire
   * (an older API omits it); treat absent as "unknown", not as zero.
   */
  processing_now?: number;
  /**
   * Failed PDFs the auto-retry cron is still working — the rows that used to
   * wear a red "failed" card with a Retry button while the system was about to
   * recover them on its own. Same scoping as `processing_now` (this project
   * plus the unrouted rows), and exactly complementary to `attention`: every
   * row counted here is one that left the failed list.
   *
   * A row leaves this count for the red list on a deterministic failure, on
   * exhausted attempts, or when the LANE ITSELF stops moving — a dead or wedged
   * cron must not hide behind a reassuring number (see RETRY_LANE_STALE_MS and
   * RETRY_LANE_CRON_LIVENESS_MS, apps/api intake/auto-retry.ts).
   *
   * Counted within the same newest-50 attention window the red list reads, so
   * a very large backlog under-reports here — the same cap the failed list has
   * always had. `processing_now` is a real COUNT and stays exact.
   *
   * The API always serializes it (0 when none). Optional on the wire because a
   * cached web bundle can outlive the Worker that serves it — read it `?? 0`.
   */
  retrying_now?: number;
}

/**
 * Response from the session-auth reparse endpoint
 * (POST /api/intake/pdfs/:id/reparse). `status` is the outcome of the re-run:
 * 'parsed' (or 'parsed_with_warnings') means recovered — the row leaves the
 * failed banner and lands in the inbox; 'failed' means it errored again
 * (often a still-ongoing upstream blip — the caller can retry shortly).
 */
export interface ReparsePdfResponse {
  daily_pdf_id: string;
  status: ParseStatus;
  rollup_recompute_error: string | null;
}

// ----- Backfill import (admin, design 2026-07-09) -----
//
// The local parser (apps/api/scripts/backfill-parse.mjs) emits these rows;
// POST /admin/backfill/import upserts them idempotently, one section per call.

export interface BackfillPcoRow {
  pco_number: string;
  description: string;
  amount_proposed_cents: number | null;
  comments: string | null;
  status: string; // 'open' | 'void' | ...
}
export interface BackfillTrancheRow {
  pco_number: string;
  bucket: string | null;
  mod_number: string | null;
  hoffman_fwa: string | null;
  approved_amount_cents: number;
}
export interface BackfillSubmissionRow {
  pco_number: string;
  submission_number: string;
  status: 'approved' | 'closed';
  mod_number: string | null;
  hoffman_fwa: string | null;
  imported_total_cents: number | null;
  imported_range_label: string | null;
}
export interface BackfillWeeklyRow {
  pco_number: string;
  submission_number: string;
  week_ending_date: string; // 'YYYY-MM-DD'
  invoice_total_cents: number;
  status: 'approved' | 'submitted';
  source_xlsx_r2_key: string;
}
export interface BackfillRosterEmployeeRow {
  name: string;
  classification: string;
  heavyjob_employee_id?: string | null;
  rate_cents?: number | null;
}
export interface BackfillRosterEquipmentRow {
  asset_id: string;
  description: string;
  rate_cents: number;
}

export type BackfillSection = 'pcos' | 'tranches' | 'submissions' | 'weeklies' | 'roster';

export interface BackfillImportRequest {
  section: BackfillSection;
  rows: unknown[];
}

/** One field an import run would overwrite (or did overwrite): the dry-run
 *  clobber-visibility surface. `from` is the value currently in the app —
 *  if it was a manual correction, this row is a revert; vet before committing. */
export interface BackfillFieldChange {
  key: string;
  field: string;
  from: unknown;
  to: unknown;
}

export interface BackfillImportResponse {
  section: BackfillSection;
  dry_run: boolean;
  inserted: number;
  updated: number;
  /** Rows matching an existing row with zero field-level differences — write skipped. */
  unchanged: number;
  skipped: number;
  errors: string[];
  /** Non-fatal review items (duplicate-tranche-insert signature). */
  warnings: string[];
  changes: BackfillFieldChange[];
}

// ============================================================================
// FOREMAN COVERAGE (missing-daily alarm)
// ============================================================================

/**
 * One watched (or watchable) foreman on the PDF Inbox's coverage card.
 *
 * `quiet_site_days` is NOT calendar days: it counts distinct dates since this
 * foreman's last daily on which some OTHER foreman filed one. That makes it a
 * measure of days the job demonstrably ran without his paperwork, and it means
 * weekends, rain-outs, and site shutdowns cost nothing.
 */
export interface ForemanCoverageEntry {
  foreman_name: string;
  /** 'YYYY-MM-DD' — his most recent daily. Always present. */
  last_report_date: string;
  quiet_site_days: number;
  /** Distinct filing dates in the 21 days ending at his last daily. */
  dailies_in_window: number;
  /** Auto-enrolled: >= 3 filing dates in that window. Only these can go quiet. */
  watched: boolean;
  status: 'ok' | 'quiet' | 'paused';
  /**
   * HOW LOUD the gap is, independent of any pause.
   *
   * `'watch'` (3..7 site-days) is the ordinary look of work still sitting in an
   * unforwarded batch — foremen send to Josh/Joey, who forward in lumps, so
   * short gaps are routine. It renders muted inside the expanded card and is
   * never amber, never counted in the headline, never emailed.
   *
   * `'attention'` (8+) has outlived the recent forwarding cycles: batches came
   * in and this gap stayed open. It drives the amber state, the headline count,
   * and the one ops email.
   */
  tier: 'none' | 'watch' | 'attention';
  /** 'YYYY-MM-DD', inclusive. Null on an open-ended pause or when not paused. */
  paused_until: string | null;
  note: string | null;
  /** ISO — when a human last paused/resumed him. Null if never touched. */
  updated_at: string | null;
}

export interface ForemanCoverageResponse {
  /** 'YYYY-MM-DD' Pacific — the day the counts were taken as of. */
  as_of: string;
  /** Site-days at which a gap becomes visible at all (watch tier). */
  watch_threshold_site_days: number;
  /** Site-days at which a gap goes amber and emails (attention tier). */
  attention_threshold_site_days: number;
  /** Every foreman with a daily in the lookback, quietest first. */
  foremen: ForemanCoverageEntry[];
  /** Quiet at EITHER tier — the total the expanded card lists. */
  quiet_count: number;
  /** The subset that is attention-tier. This is the number the headline says. */
  attention_count: number;
  paused_count: number;
  watched_count: number;
}

export interface PauseForemanCoverageRequest {
  foreman_name: string;
  /** 'YYYY-MM-DD', inclusive. Omit/null for an open-ended pause. */
  paused_until?: string | null;
  note?: string | null;
}

export interface ResumeForemanCoverageRequest {
  foreman_name: string;
}

/** Both mutations answer with the affected foreman's freshly-derived entry. */
export interface ForemanCoverageMutationResponse {
  foreman: ForemanCoverageEntry;
}
